import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import { 
  Mic, 
  MicOff, 
  Settings, 
  Video,
  VideoOff,
  Users,
  Volume2,
  RotateCcw,
  SwitchCamera,
  PhoneOff,
  Loader2,
  AlertCircle,
  MonitorPlay,
  ChevronUp,
  Share2,
  Crosshair
} from "lucide-react";
import { toast } from "sonner";
import {
  RoomContext,
  RoomAudioRenderer,
  useLocalParticipant,
  useRoomContext,
  useTracks,
  VideoTrack,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { ConnectionState, RoomEvent, Track, Room } from "livekit-client";
import { useScreenProtection } from "@/hooks/useScreenProtection";
import ScreenProtectionOverlay, { ProtectedContent } from "@/components/ScreenProtectionOverlay";
import LiveReactionLayer from "@/components/LiveReactionLayer";
import ShowMeDonationDollarLayer, {
  normalizeShowMeDonationDollarCount,
} from "@/components/ShowMeDonationDollarLayer";
import StreamDiagnostics from "@/components/StreamDiagnostics";
import EducationQuestionQueue, { CreatorAudienceSpeakerControls } from "@/components/EducationQuestionQueue";
import CreatorSettingsPortal from "@/components/CreatorSettingsPortal";
import { getEventExperience, isVisibleEventCategory } from "@/config/eventCategories";
import {
  getNewEnergyReactionCount,
  SHOWME_ENERGY_START_REACTION_COUNT,
} from "@/utils/showMeEnergy";
import { isRelayAnnouncementAccepted } from "@/utils/streamRelay";
import {
  CREATOR_CAMERA_BACKGROUND_FIT,
  getCameraCaptureProfile,
  getCameraDisplayFit,
  getCameraMediaConstraints,
  releaseCameraPreview,
  resetCameraZoom,
} from "@/utils/cameraCapture";
import {
  hasOutboundAudioProgress,
  hasOutboundVideoData,
  hasOutboundVideoProgress,
  summarizeOutboundAudioStats,
  summarizeOutboundVideoStats,
} from "@/utils/outboundVideoHealth";
import {
  BROADCAST_AUDIO_MODES,
  BROADCAST_AUDIO_MODE_RAW,
  calculateVideoBitrateKbps,
  getBroadcastAudioCaptureOptions,
  getBroadcastAudioPublishOptions,
  getBroadcastVideoEncoding,
} from "@/utils/broadcastQuality";
import {
  CREATOR_AUDIO_PROCESSOR_NAME,
  CreatorAudioProcessor,
  getCreatorAudioParameters,
} from "@/utils/creatorAudioProcessor";
import useDeviceVideoOrientation, { readDeviceVideoOrientation } from "@/hooks/useDeviceVideoOrientation";
import {
  CreatorTipNotificationStack,
  CreatorTitheBadge,
  LiveChatLane,
  LiveShareSheet,
  LiveTopBar,
  useCreatorTipNotifications,
  useLiveViewportLock,
  readUsableLiveViewportDimensions,
} from "@/components/LiveExperienceChrome";
import { mergeChatMessages } from "@/utils/chatMessages";
import {
  getMediaRecoveryDelay,
  isMediaTrackHealthy,
} from "@/utils/mobileStreamRecovery";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const IS_IOS_WEBKIT = (() => {
  const userAgent = window.navigator?.userAgent || "";
  const platform = window.navigator?.platform || "";
  const touchMac = platform === "MacIntel" && window.navigator?.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(userAgent) || touchMac;
})();

const RESOLUTION_MAP = {
  auto: "Auto",
  max: "AWS Max",
  "1080p": "1080p",
  "720p": "720p",
  "360p": "360p",
};

const REACTION_TYPE_EMOJIS = {
  heart: "\u2764\ufe0f",
  clap: "\ud83d\udc4f",
  fire: "\ud83d\udd25",
  laugh: "\ud83d\ude02",
  wow: "\ud83d\ude2e",
};

const reuseEqualSnapshot = (current, next) => {
  if (current === next) return current;
  try {
    return JSON.stringify(current) === JSON.stringify(next) ? current : next;
  } catch {
    return next;
  }
};

const getShowMeFocusCapabilities = (track) => {
  const capabilities = track?.getCapabilities?.() || {};
  const settings = track?.getSettings?.() || {};
  const focusModes = Array.isArray(capabilities.focusMode) ? capabilities.focusMode : [];
  const zoom = capabilities.zoom;
  return {
    autoFocusSupported: focusModes.includes("continuous") || focusModes.includes("single-shot"),
    zoomSupported: Boolean(zoom && Number(zoom.max) > Number(zoom.min)),
    zoomMin: Number(zoom?.min ?? 1),
    zoomMax: Number(zoom?.max ?? 1),
    zoomStep: Number(zoom?.step ?? 0.1),
    zoom: Number(settings.zoom ?? zoom?.min ?? 1),
    torchSupported: capabilities.torch === true,
    torchOn: settings.torch === true,
  };
};

const bindShowMeFocus = async (track, controlRef, onCapabilities) => {
  if (!track) return;
  const capabilities = getShowMeFocusCapabilities(track);
  const rawCapabilities = track.getCapabilities?.() || {};
  const focusModes = Array.isArray(rawCapabilities.focusMode) ? rawCapabilities.focusMode : [];
  const autoFocus = async () => {
    if (!capabilities.autoFocusSupported) return false;
    const focusMode = focusModes.includes("continuous") ? "continuous" : "single-shot";
    await track.applyConstraints({ advanced: [{ focusMode }] });
    return true;
  };
  try { await autoFocus(); } catch (error) { console.warn('[ShowMeFocus] autofocus unavailable:', error); }
  onCapabilities?.(getShowMeFocusCapabilities(track));
  if (controlRef) {
    controlRef.current = {
      autoFocus,
      setZoom: async (zoom) => {
        if (!capabilities.zoomSupported) return false;
        const bounded = Math.min(capabilities.zoomMax, Math.max(capabilities.zoomMin, Number(zoom)));
        await track.applyConstraints({ advanced: [{ zoom: bounded }] });
        onCapabilities?.(getShowMeFocusCapabilities(track));
        return true;
      },
      setTorch: async (enabled) => {
        if (rawCapabilities.torch !== true) return false;
        await track.applyConstraints({ advanced: [{ torch: Boolean(enabled) }] });
        onCapabilities?.(getShowMeFocusCapabilities(track));
        return true;
      },
    };
  }
};

const EMPTY_FOCUS_CAPABILITIES = {
  autoFocusSupported: false,
  zoomSupported: false,
  zoomMin: 1,
  zoomMax: 1,
  zoomStep: 0.1,
  zoom: 1,
  torchSupported: false,
  torchOn: false,
};

// ============================================
// SMOOTH REACTION COMPONENT (Right Side Only)
// Floats upward from bottom and leaves the screen
// Uses CSS animation for smooth performance
// ============================================
const SmoothReaction = ({ emoji, startX, onComplete }) => {
  const animationDuration = 3500; // total lifetime (ms)
  const floatDistance = 800; // px risen before leaving screen
  // Per-instance sway/rotation so a burst doesn't move in lockstep.
  const cfg = useRef({
    sway: (Math.random() - 0.5) * 90,
    rot: (Math.random() - 0.5) * 26,
  }).current;

  useEffect(() => {
    const timer = setTimeout(onComplete, animationDuration);
    return () => clearTimeout(timer);
  }, [onComplete, animationDuration]);

  return (
    <div
      className="fixed pointer-events-none"
      style={{
        right: `${startX}%`,
        bottom: '8%',
        zIndex: 9999,
        willChange: 'transform, opacity',
        animation: `floatUpAndFade ${animationDuration}ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards`,
        '--sway': `${cfg.sway}px`,
        '--rot': `${cfg.rot}deg`,
      }}
    >
      <span 
        className="text-5xl md:text-6xl block"
        style={{
          filter: 'drop-shadow(0 0 12px rgba(59, 130, 246, 0.7)) drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
        }}
      >
        {emoji}
      </span>
      
      {/* Inline keyframes for the animation */}
      <style>{`
        @keyframes floatUpAndFade {
          0%   { opacity: 0; transform: translate(0, 0) scale(0.6) rotate(0deg); }
          10%  { opacity: 1; transform: translate(calc(var(--sway) * 0.2), -${floatDistance * 0.08}px) scale(1.12) rotate(calc(var(--rot) * 0.3)); }
          30%  { opacity: 1; transform: translate(calc(var(--sway) * 0.5), -${floatDistance * 0.3}px) scale(1) rotate(calc(var(--rot) * 0.6)); }
          70%  { opacity: 0.85; transform: translate(calc(var(--sway) * 0.85), -${floatDistance * 0.7}px) scale(0.92) rotate(var(--rot)); }
          100% { opacity: 0; transform: translate(var(--sway), -${floatDistance}px) scale(0.72) rotate(var(--rot)); }
        }
      `}</style>
    </div>
  );
};

// ============================================
// RIGHT-SIDE REACTION LAYER (Max 20% from edge)
// Reactions are rendered with 'fixed' position to escape any container clipping
// ============================================
const RightSideReactionLayer = ({ reactions }) => {
  const [activeReactions, setActiveReactions] = useState([]);
  const maxReactions = 25;
  const lastProcessedRef = useRef(null);

  useEffect(() => {
    if (reactions.length > 0) {
      const latest = reactions[reactions.length - 1];
      if (latest.id !== lastProcessedRef.current) {
        lastProcessedRef.current = latest.id;
        // Functional update + cap check inside — the old `activeReactions.length`
        // dependency re-ran this effect and could drop reactions at the cap.
        setActiveReactions(prev => {
          if (prev.length >= maxReactions) return prev;
          const startX = 2 + Math.random() * 16;
          return [...prev, { ...latest, startX }];
        });
      }
    }
  }, [reactions]);

  const handleComplete = useCallback((id) => {
    setActiveReactions(prev => prev.filter(r => r.id !== id));
  }, []);

  // Render reactions - they use 'fixed' positioning so no container needed
  return (
    <>
      {activeReactions.map((reaction) => (
        <SmoothReaction
          key={reaction.id}
          emoji={reaction.emoji}
          startX={reaction.startX}
          onComplete={() => handleComplete(reaction.id)}
        />
      ))}
    </>
  );
};

// ============================================
// SHOWMEENERGY
// Reaction energy transitions from electric blue to bright purple
// ============================================
const ShowMeEnergy = ({ reactionCount }) => {
  const [glowIntensity, setGlowIntensity] = useState(0);
  const processedReactionCountRef = useRef(0);
  const cooldownRef = useRef(null);
  const decayRef = useRef(null);

  // Each reaction restarts the inactivity window. The glow only begins
  // cooling after 30 seconds without another reaction.
  useEffect(() => {
    if (reactionCount <= 0) {
      processedReactionCountRef.current = 0;
      return undefined;
    }

    clearTimeout(cooldownRef.current);
    clearInterval(decayRef.current);
    const previousReactionCount = processedReactionCountRef.current;
    const newEnergyReactionCount = getNewEnergyReactionCount(
      previousReactionCount,
      reactionCount,
    );
    processedReactionCountRef.current = reactionCount;
    if (newEnergyReactionCount > 0) {
      setGlowIntensity((previous) => Math.min(previous + (newEnergyReactionCount * 0.15), 1));
    }

    if (reactionCount < SHOWME_ENERGY_START_REACTION_COUNT) return undefined;

    cooldownRef.current = setTimeout(() => {
      decayRef.current = setInterval(() => {
        setGlowIntensity((previous) => {
          const next = Math.max(previous - 0.02, 0);
          if (next === 0) clearInterval(decayRef.current);
          return next;
        });
      }, 100);
    }, 30000);

    return () => {
      clearTimeout(cooldownRef.current);
      clearInterval(decayRef.current);
    };
  }, [reactionCount]);
  
  if (glowIntensity < 0.05) return null;
  
  const colorProgress = Math.max(0, Math.min(1, (glowIntensity - 0.05) / 0.95));
  const startColor = [17, 24, 255];
  const endColor = [238, 0, 255];
  const glowColor = startColor.map((channel, index) =>
    Math.round(channel + ((endColor[index] - channel) * colorProgress)),
  ).join(', ');
  const opacity = glowIntensity * 0.78;
  const spread = 20 + glowIntensity * 40;
  
  return (
    <div 
      className="absolute inset-0 pointer-events-none z-20"
      data-testid="showme-energy"
      data-energy-level={Math.round(glowIntensity * 100)}
      aria-hidden="true"
      style={{
        boxShadow: `
          inset 0 0 ${spread}px rgba(${glowColor}, ${opacity}),
          inset 0 0 ${spread * 2}px rgba(${glowColor}, ${opacity * 0.5}),
          inset 0 0 ${spread * 3}px rgba(${glowColor}, ${opacity * 0.25})
        `,
        transition: 'box-shadow 0.3s ease-out',
      }}
    />
  );
};

// ============================================
// CONNECTION STATUS DOT (Top Right)
// ============================================
const ConnectionStatusDot = ({ connected }) => {
  return (
    <div 
      className="absolute top-4 right-4 z-50"
      title={connected ? 'Connected' : 'Connecting...'}
    >
      <div 
        className={`w-3 h-3 rounded-full ${connected ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`}
        style={{
          boxShadow: connected 
            ? '0 0 8px rgba(34, 197, 94, 0.6), 0 0 16px rgba(34, 197, 94, 0.3)'
            : '0 0 8px rgba(234, 179, 8, 0.6)',
        }}
      />
    </div>
  );
};

// ============================================
// MINIMAL CHAT DISPLAY (Left Side)
// ============================================
const MinimalChatLane = ({ messages, isReligion }) => {
  const [displayMessages, setDisplayMessages] = useState([]);

  useEffect(() => {
    if (messages.length > 0) {
      const latestMsg = messages[messages.length - 1];
      // Functional update — the old closure over displayMessages could go stale
      // and silently drop incoming chat messages.
      setDisplayMessages(prev => {
        if (prev.find(m => m.id === latestMsg.id)) return prev;
        return [...prev.slice(-4), { ...latestMsg, addedAt: Date.now() }];
      });
    }
  }, [messages]);

  useEffect(() => {
    const cleanup = setInterval(() => {
      const now = Date.now();
      setDisplayMessages(prev => prev.filter(msg => now - msg.addedAt < 6000));
    }, 500);
    return () => clearInterval(cleanup);
  }, []);

  const getAvatarColor = (username) => {
    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#10b981', '#f59e0b'];
    const hash = username.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className="absolute left-4 bottom-[8%] w-[40%] max-w-md pointer-events-none z-30">
      {isReligion && (
        <div className="mb-1.5">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-blue-500/15 border border-blue-400/25 text-blue-300 text-[11px] font-bold tracking-wide" data-testid="congregation-badge-creator">ShowMeCongregation</span>
        </div>
      )}
      <div className="flex flex-col gap-2">
        {displayMessages.map((msg) => {
          const age = Date.now() - msg.addedAt;
          const isExiting = age > 4500;
          
          return (
            <div
              key={msg.id}
              className="flex items-center gap-2 px-3 py-2 rounded-full bg-black/30 max-w-fit"
              style={{
                opacity: isExiting ? 0 : 0.9,
                transform: isExiting ? 'translateX(-20px)' : 'translateX(0)',
                transition: 'all 0.5s ease-out',
              }}
            >
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                style={{ backgroundColor: getAvatarColor(msg.username) }}
              >
                {msg.username.charAt(0).toUpperCase()}
              </div>
              <span className="text-white text-sm font-medium truncate max-w-[200px]">
                {msg.message}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// AUDIO SETTINGS DROPDOWN
// ============================================
const AudioSliderControl = ({
  icon: Icon,
  label,
  value,
  onChange,
  color = "#3b82f6",
  max = 100,
  neutralAt,
}) => {
  const updateValue = (event) => onChange(Number(event.currentTarget.value));
  const fillPercent = Math.min(100, Math.max(0, (Number(value) / max) * 100));
  const isAmplified = Number.isFinite(neutralAt) && value > neutralAt;

  return (
    <label className="block select-none space-y-2 py-1">
      <span className="flex items-center gap-1">
        <Icon className="h-4 w-4 text-gray-400" />
        <span className="text-sm text-gray-300">
          {label} {value}%{isAmplified ? " · amplified" : ""}
        </span>
      </span>
      <span className="flex min-h-10 items-center">
        <input
          type="range"
          min="0"
          max={max}
          step="1"
          value={value}
          aria-label={`${label} ${value}%`}
          onInput={updateValue}
          className="h-3 w-full cursor-pointer touch-none appearance-none rounded-full bg-gray-700"
          style={{
            accentColor: color,
            background: `linear-gradient(to right, ${color} 0%, ${color} ${fillPercent}%, #374151 ${fillPercent}%, #374151 100%)`,
          }}
        />
      </span>
    </label>
  );
};

const AudioSettingsDropdown = ({
  isOpen, onClose, balance, setBalance,
  treble, setTreble, bass, setBass, onReset,
  videoQuality, setVideoQuality,
  audioMode, setAudioMode,
  settingsLocked, qualityStatus,
}) => {
  if (!isOpen) return null;

  return (
    <CreatorSettingsPortal>
    <div className="fixed inset-0 z-[120]" data-testid="creator-settings-sheet" data-creator-front-layer="true">
      <button type="button" className="absolute inset-0 h-full w-full bg-black/50 backdrop-blur-sm" onClick={onClose} aria-label="Close settings" />
      <section className="absolute inset-x-0 bottom-0 max-h-[76%] overflow-y-auto rounded-t-[1.65rem] border-t border-white/15 bg-white/[0.07] pb-[max(env(safe-area-inset-bottom),1.5rem)] shadow-2xl backdrop-blur-3xl animate-in slide-in-from-bottom duration-300 sm:left-1/2 sm:max-w-xl sm:-translate-x-1/2">
        <div className="flex justify-center py-3"><span className="h-1.5 w-11 rounded-full bg-gray-600" /></div>
        <div className="flex items-center justify-between px-5 pb-4">
          <div>
            <h3 className="text-lg font-extrabold text-white">Live settings</h3>
            <p className="text-xs text-gray-400">Stream quality and audio levels</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-bold text-white">Done</button>
        </div>
        <div className="space-y-4 border-t border-white/10 px-5 py-4">
          <div className="rounded-xl border border-blue-400/20 bg-blue-500/10 px-3 py-2" data-testid="broadcast-quality-status">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="font-bold text-blue-200">Broadcast quality</span>
              <span className="font-mono uppercase text-blue-300">{qualityStatus?.connectionQuality || 'pre-live'}</span>
            </div>
            <p className="mt-1 text-[10px] text-gray-400">
              {settingsLocked
                ? `${qualityStatus?.width || 0}×${qualityStatus?.height || 0} · ${qualityStatus?.fps || 0} fps · ${qualityStatus?.bitrateKbps || 0} kbps`
                : 'Camera framing stays native; encoder and audio choices lock when you go live.'}
            </p>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400"><Mic className="h-4 w-4" /> Audio profile</div>
            <div className="grid grid-cols-1 gap-1 rounded-xl bg-black/30 p-1" data-testid="broadcast-audio-mode-picker">
              {BROADCAST_AUDIO_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  disabled={settingsLocked}
                  onClick={() => setAudioMode(mode.id)}
                  className={`rounded-lg px-2 py-2 text-left transition disabled:cursor-not-allowed disabled:opacity-60 ${audioMode === mode.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-white/5'}`}
                  data-testid={`broadcast-audio-${mode.id}`}
                >
                  <span className="block text-[11px] font-extrabold">{mode.label}</span>
                  <span className="block text-[9px] opacity-75">{mode.detail}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400"><MonitorPlay className="h-4 w-4" /> Video quality</div>
            <div className="grid grid-cols-5 gap-1 rounded-xl bg-black/30 p-1">
              {Object.keys(RESOLUTION_MAP).map((quality) => (
                <button
                  key={quality}
                  type="button"
                  disabled={settingsLocked}
                  onClick={() => setVideoQuality(quality)}
                  className={`rounded-lg px-1 py-2 text-[11px] font-bold uppercase transition disabled:cursor-not-allowed disabled:opacity-60 ${videoQuality === quality ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-white/5'}`}
                  data-testid={`resolution-${quality}`}
                >
                  {RESOLUTION_MAP[quality]}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[10px] text-gray-500">AWS Max delivers the IVS maximum: 1080p at up to 60 fps.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <AudioSliderControl icon={Volume2} label="Balance" value={balance} onChange={setBalance} color="#8b5cf6" />
            <AudioSliderControl icon={Volume2} label="Treble" value={treble} onChange={setTreble} color="#10b981" />
            <AudioSliderControl icon={Volume2} label="Bass" value={bass} onChange={setBass} color="#f59e0b" />
          </div>
          <button onClick={onReset} className="ml-auto flex items-center gap-1 rounded-lg bg-white/10 px-3 py-2 text-xs font-bold text-white hover:bg-white/15">
            <RotateCcw className="w-3 h-3" /> Reset audio
          </button>
        </div>
      </section>
    </div>
    </CreatorSettingsPortal>
  );
};

// ============================================
// LIVEKIT STREAM PUBLISHER
// ============================================
// LiveKitRoom's video={false} connection hook actively disables the camera
// after joining. That races the iPhone preview-track handoff and produces an
// audio-only room until the creator refreshes. This host connects the room but
// leaves camera and microphone ownership entirely to StreamPublisher.
const ManualCreatorRoom = ({
  serverUrl,
  token,
  connectOptions,
  options,
  onConnected,
  onDisconnected,
  onError,
  children,
}) => {
  const [room] = useState(() => new Room(options));
  const connectOptionsRef = useRef(connectOptions);
  const callbacksRef = useRef({ onConnected, onDisconnected, onError });

  useEffect(() => {
    callbacksRef.current = { onConnected, onDisconnected, onError };
  }, [onConnected, onDisconnected, onError]);

  useEffect(() => {
    let disposed = false;
    const handleConnected = () => {
      if (!disposed) callbacksRef.current.onConnected?.();
    };
    const handleDisconnected = () => {
      if (!disposed) callbacksRef.current.onDisconnected?.();
    };

    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.connect(serverUrl, token, connectOptionsRef.current).catch((error) => {
      if (!disposed) callbacksRef.current.onError?.(error);
    });

    return () => {
      disposed = true;
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.disconnect();
    };
  }, [room, serverUrl, token]);

  return <RoomContext.Provider value={room}>{children}</RoomContext.Provider>;
};

const captureLiveSessionProfile = (
  orientation,
  videoQuality,
  audioMode,
) => ({
  orientation: orientation === 'landscape' ? 'landscape' : 'portrait',
  videoQuality,
  audioMode,
  viewport: readUsableLiveViewportDimensions(),
});

const StreamPublisher = React.memo(({
  isCameraOn,
  isMicOn,
  facingMode,
  videoQuality,
  orientation,
  eventId,
  audioMode,
  speakerVolume,
  micVolume,
  balance,
  treble,
  bass,
  onCameraSwitchTrigger,
  onFacingModeChange,
  showMeFocusRef,
  onShowMeFocusCapabilities,
  onPublisherReady,
  onQualityStatusChange,
}) => {
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();
  const tracks = useTracks([Track.Source.Camera, Track.Source.Microphone]);
  const [currentFacingMode, setCurrentFacingMode] = useState(facingMode || 'user'); // 'user' = front, 'environment' = back
  const [isSwitching, setIsSwitching] = useState(false);
  const [isRoomConnected, setIsRoomConnected] = useState(
    room?.state === ConnectionState.Connected
  );
  const [hasOutboundVideo, setHasOutboundVideo] = useState(false);
  const publisherReadyReportedRef = useRef(false);
  const outboundRecoveryCountRef = useRef(0);
  const outboundRecoveryInProgressRef = useRef(false);
  const mediaRecoveryTimerRef = useRef(null);
  const mediaRecoveryInProgressRef = useRef(false);
  const audioProcessorRef = useRef(null);
  const audioSettingsRef = useRef({ micVolume, balance, treble, bass });
  audioSettingsRef.current = { micVolume, balance, treble, bass };
  
  const localVideoTrack = tracks.find(
    (track) => track.participant.isLocal && track.source === Track.Source.Camera
  );
  const localCameraPublication = localVideoTrack?.publication;
  const localMediaStreamTrack = localCameraPublication?.track?.mediaStreamTrack;
  const localAudioTrack = tracks.find(
    (track) => track.participant.isLocal && track.source === Track.Source.Microphone
  );
  const localMicrophonePublication = localAudioTrack?.publication;
  const localMicrophoneTrack = localMicrophonePublication?.audioTrack
    || localMicrophonePublication?.track;
  const localMicrophoneMediaStreamTrack = localMicrophoneTrack?.mediaStreamTrack;
  const localCameraFit = getCameraDisplayFit(
    orientation,
    localMediaStreamTrack?.getSettings?.() || {},
  );

  // LiveKitRoom renders its children while the WebSocket is still connecting.
  // Do not transfer the working preview camera until LiveKit can accept tracks.
  useEffect(() => {
    if (!room) return undefined;

    const syncConnectionState = () => {
      setIsRoomConnected(room.state === ConnectionState.Connected);
    };

    syncConnectionState();
    room.on(RoomEvent.Connected, syncConnectionState);
    room.on(RoomEvent.Disconnected, syncConnectionState);
    room.on(RoomEvent.Reconnecting, syncConnectionState);
    room.on(RoomEvent.Reconnected, syncConnectionState);

    return () => {
      room.off(RoomEvent.Connected, syncConnectionState);
      room.off(RoomEvent.Disconnected, syncConnectionState);
      room.off(RoomEvent.Reconnecting, syncConnectionState);
      room.off(RoomEvent.Reconnected, syncConnectionState);
    };
  }, [room]);

  const liveCameraCaptureOptions = useCallback(() => {
    const profile = getCameraCaptureProfile(videoQuality, orientation);
    return {
      facingMode: currentFacingMode,
      frameRate: profile.frameRate,
    };
  }, [currentFacingMode, orientation, videoQuality]);

  const forceRestartLiveCamera = useCallback(async (reason = 'camera recovery') => {
    if (
      !localParticipant ||
      !isRoomConnected ||
      !isCameraOn ||
      outboundRecoveryInProgressRef.current
    ) return false;

    outboundRecoveryInProgressRef.current = true;
    setHasOutboundVideo(false);
    try {
      const publication = localParticipant.getTrackPublication(Track.Source.Camera);
      console.warn(`[Camera] ${reason}; restarting the current camera`);
      if (publication?.videoTrack?.restartTrack) {
        await publication.videoTrack.restartTrack(
          getCameraMediaConstraints(videoQuality, orientation, currentFacingMode),
        );
      } else {
        if (publication) await localParticipant.setCameraEnabled(false).catch(() => {});
        await localParticipant.setCameraEnabled(true, liveCameraCaptureOptions());
      }

      const restartedTrack = localParticipant
        .getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack;
      await resetCameraZoom(restartedTrack).catch((error) => {
        console.warn('[Camera] zoom reset unavailable after recovery:', error);
      });
      return true;
    } catch (error) {
      console.error('[Camera] forced camera restart failed:', error);
      return false;
    } finally {
      outboundRecoveryInProgressRef.current = false;
    }
  }, [
    currentFacingMode,
    isCameraOn,
    isRoomConnected,
    liveCameraCaptureOptions,
    localParticipant,
    orientation,
    videoQuality,
  ]);

  const recoverLiveCamera = useCallback(async () => {
    if (!localParticipant || !isRoomConnected || !isCameraOn) return false;

    const publication = localParticipant.getTrackPublication(Track.Source.Camera);
    const mediaTrack = publication?.track?.mediaStreamTrack;
    if (
      publication?.track &&
      isMediaTrackHealthy(mediaTrack, publication.isMuted)
    ) {
      return true;
    }

    try {
      // A stale publication can leave LiveKit's internal camera-enabled flag
      // true even though no usable track exists. Clear it before reacquiring.
      if (publication) {
        await localParticipant.setCameraEnabled(false).catch(() => {});
      }
      await localParticipant.setCameraEnabled(true, liveCameraCaptureOptions());
      const recoveredTrack = localParticipant
        .getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack;
      await resetCameraZoom(recoveredTrack).catch((error) => {
        console.warn('[Camera] zoom reset unavailable:', error);
      });
      return true;
    } catch (error) {
      console.error('[Camera] live camera recovery failed:', error);
      return false;
    }
  }, [isCameraOn, isRoomConnected, liveCameraCaptureOptions, localParticipant]);

  const recoverLiveMicrophone = useCallback(async (force = false) => {
    if (!localParticipant || !isRoomConnected || !isMicOn) return false;

    const publication = localParticipant.getTrackPublication(Track.Source.Microphone);
    const publishedTrack = publication?.audioTrack || publication?.track;
    const mediaTrack = publishedTrack?.mediaStreamTrack;
    if (!force && isMediaTrackHealthy(mediaTrack, publication?.isMuted)) {
      await audioProcessorRef.current?.resume?.();
      return true;
    }

    try {
      if (publication) {
        await localParticipant.setMicrophoneEnabled(false).catch(() => {});
      }
      await localParticipant.setMicrophoneEnabled(
        true,
        getBroadcastAudioCaptureOptions(audioMode),
        getBroadcastAudioPublishOptions(audioMode),
      );
      const recoveredPublication = localParticipant.getTrackPublication(Track.Source.Microphone);
      const recoveredTrack = (recoveredPublication?.audioTrack || recoveredPublication?.track)
        ?.mediaStreamTrack;
      if (recoveredTrack && "contentHint" in recoveredTrack) recoveredTrack.contentHint = "music";
      await audioProcessorRef.current?.resume?.();
      return isMediaTrackHealthy(recoveredTrack, recoveredPublication?.isMuted);
    } catch (error) {
      console.error('[Audio] live microphone recovery failed:', error);
      return false;
    }
  }, [audioMode, isMicOn, isRoomConnected, localParticipant]);

  // A browser can leave an audio MediaStreamTrack marked "live" after an OS
  // interruption while its WebRTC sender emits no RTP. With DTX disabled,
  // outbound bytes should keep advancing even during silence; reacquire only
  // after three consecutive stalled samples and keep the room/video mounted.
  useEffect(() => {
    if (!isRoomConnected || !isMicOn || typeof localMicrophoneTrack?.getRTCStatsReport !== 'function') {
      return undefined;
    }
    let cancelled = false;
    let sampling = false;
    let previousSample = null;
    let stalledSamples = 0;

    const verifyOutboundAudio = async () => {
      if (cancelled || sampling || document.visibilityState === 'hidden') return;
      sampling = true;
      try {
        const report = await localMicrophoneTrack.getRTCStatsReport();
        if (cancelled) return;
        const sample = summarizeOutboundAudioStats(report);
        if (!sample.reportAvailable || !sample.outboundAudioFound) return;
        if (!previousSample || hasOutboundAudioProgress(previousSample, sample)) {
          stalledSamples = 0;
        } else {
          stalledSamples += 1;
        }
        previousSample = sample;
        if (stalledSamples >= 3) {
          stalledSamples = 0;
          previousSample = null;
          await recoverLiveMicrophone(true);
        }
      } catch (error) {
        console.warn('[Audio] outbound microphone health check failed:', error);
      } finally {
        sampling = false;
      }
    };

    const initialCheck = window.setTimeout(verifyOutboundAudio, 1500);
    const interval = window.setInterval(verifyOutboundAudio, 2000);
    return () => {
      cancelled = true;
      window.clearTimeout(initialCheck);
      window.clearInterval(interval);
    };
  }, [isMicOn, isRoomConnected, localMicrophoneTrack, recoverLiveMicrophone]);

  // Verify the camera shortly after connection. First Go Live now follows the
  // same fresh-capture path as a successful creator refresh.
  useEffect(() => {
    if (!isRoomConnected || !isCameraOn) return undefined;

    const verifyCamera = () => recoverLiveCamera();
    const initialCheck = window.setTimeout(verifyCamera, 1800);
    const interval = window.setInterval(verifyCamera, 4000);

    return () => {
      window.clearTimeout(initialCheck);
      window.clearInterval(interval);
    };
  }, [isCameraOn, isRoomConnected, recoverLiveCamera]);

  // A MediaStreamTrack can remain "live" and unmuted on iOS Safari while its
  // WebRTC sender encodes zero frames. Verify real outbound RTP before allowing
  // viewers into the room. If the handoff stalls, restart the same lens with
  // the selected resolution/orientation; this automates the repair that occurs
  // when a creator manually switches cameras and switches back.
  useEffect(() => {
    if (!isRoomConnected || !isCameraOn) {
      setHasOutboundVideo(false);
      outboundRecoveryCountRef.current = 0;
      return undefined;
    }

    const videoTrack = localCameraPublication?.videoTrack || localCameraPublication?.track;
    if (typeof videoTrack?.getRTCStatsReport !== 'function') return undefined;

    let cancelled = false;
    let sampling = false;
    let staleSamples = 0;
    let unsupportedSamples = 0;
    let previouslyReady = false;
    let previousSample = null;
    let previousSampleAt = 0;
    let lastQualityReportAt = 0;
    let lastTelemetryReportAt = 0;

    const sampleOutboundVideo = async () => {
      if (cancelled || sampling || outboundRecoveryInProgressRef.current) return;
      sampling = true;
      try {
        const report = await videoTrack.getRTCStatsReport();
        if (cancelled) return;
        const sample = summarizeOutboundVideoStats(report);

        if (!sample.reportAvailable || !sample.outboundVideoFound) {
          unsupportedSamples += 1;
          if (unsupportedSamples >= 3 && outboundRecoveryCountRef.current < 2) {
            unsupportedSamples = 0;
            outboundRecoveryCountRef.current += 1;
            await forceRestartLiveCamera('outbound video sender is missing');
          } else if (unsupportedSamples >= 5 && outboundRecoveryCountRef.current >= 2) {
            // Do not permanently block uncommon browsers that omit sender RTP
            // statistics after both automatic repair attempts.
            setHasOutboundVideo(true);
          }
          return;
        }

        unsupportedSamples = 0;
        const isSending = hasOutboundVideoData(sample);
        const isAdvancing = hasOutboundVideoProgress(previousSample, sample);
        const sampleAt = Date.now();
        const bitrateKbps = previousSample
          ? calculateVideoBitrateKbps(previousSample, sample, sampleAt - previousSampleAt)
          : 0;
        if ((!previouslyReady && isSending) || (previouslyReady && isAdvancing)) {
          previouslyReady = true;
          staleSamples = 0;
          setHasOutboundVideo(true);
        } else {
          staleSamples += 1;
        }
        previousSample = sample;
        previousSampleAt = sampleAt;

        if (sampleAt - lastQualityReportAt >= 5000) {
          lastQualityReportAt = sampleAt;
          const settings = localMediaStreamTrack?.getSettings?.() || {};
          const qualityStatus = {
            connectionQuality: String(localParticipant?.connectionQuality || 'unknown').toLowerCase(),
            publishingLayer: 'automatic',
            width: Number(settings.width || sample.frameWidth || 0),
            height: Number(settings.height || sample.frameHeight || 0),
            fps: Math.round(Number(sample.framesPerSecond || settings.frameRate || 0)),
            bitrateKbps,
          };
          onQualityStatusChange?.(qualityStatus);
          if (eventId && sampleAt - lastTelemetryReportAt >= 30000) {
            lastTelemetryReportAt = sampleAt;
            axiosInstance.post(`/events/${eventId}/stream-quality`, {
              connection_quality: qualityStatus.connectionQuality,
              publishing_layer: qualityStatus.publishingLayer,
              width: qualityStatus.width,
              height: qualityStatus.height,
              fps: qualityStatus.fps,
              bitrate_kbps: qualityStatus.bitrateKbps,
              audio_mode: audioMode,
            }).catch((error) => {
              console.debug('[Quality] telemetry sample skipped:', error?.response?.status || error?.message);
            });
          }
        }

        const staleLimit = previouslyReady ? 8 : 3;
        if (staleSamples < staleLimit || outboundRecoveryCountRef.current >= 2) return;

        staleSamples = 0;
        previousSample = null;
        previouslyReady = false;
        outboundRecoveryCountRef.current += 1;
        const restarted = await forceRestartLiveCamera('outbound video is sending zero frames');
        if (!restarted && outboundRecoveryCountRef.current >= 2) {
          toast.error('The camera did not start sending video. Turn the camera off and back on.');
        }
      } catch (error) {
        console.warn('[Camera] outbound video health check failed:', error);
      } finally {
        sampling = false;
      }
    };

    const initialCheck = window.setTimeout(sampleOutboundVideo, 700);
    const interval = window.setInterval(sampleOutboundVideo, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(initialCheck);
      window.clearInterval(interval);
    };
  }, [
    forceRestartLiveCamera,
    isCameraOn,
    isRoomConnected,
    localCameraPublication,
    localMediaStreamTrack,
    localParticipant,
    onQualityStatusChange,
    eventId,
    audioMode,
  ]);

  useEffect(() => {
    if (!localMediaStreamTrack) return undefined;
    const reportSettings = () => {
      const settings = localMediaStreamTrack.getSettings?.() || {};
      console.info('[Camera] published track settings', {
        orientation,
        selectedFacingMode: currentFacingMode,
        settings,
      });
    };
    reportSettings();
    const timer = window.setTimeout(reportSettings, 800);
    return () => window.clearTimeout(timer);
  }, [currentFacingMode, localMediaStreamTrack, orientation]);

  // Announce the stream only after LiveKit confirms an unmuted camera. Retry a
  // transient server acknowledgement instead of leaving viewers in an
  // audio-only session for the rest of the broadcast.
  useEffect(() => {
    if (
      publisherReadyReportedRef.current ||
      !isRoomConnected ||
      !isCameraOn ||
      !hasOutboundVideo ||
      localMediaStreamTrack?.readyState !== 'live' ||
      localCameraPublication?.isMuted
    ) return;

    let cancelled = false;
    let retryTimer = null;
    let retryCount = 0;
    const announcePublisher = async () => {
      if (cancelled || publisherReadyReportedRef.current) return;

      try {
        const announced = onPublisherReady ? await onPublisherReady() : true;
        if (announced) {
          publisherReadyReportedRef.current = true;
          return;
        }
      } catch (error) {
        console.warn('[LiveKit] publisher announcement failed:', error);
      }
      retryCount += 1;
      retryTimer = window.setTimeout(
        announcePublisher,
        Math.min(750 * Math.pow(2, retryCount), 6000),
      );
    };
    announcePublisher();

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
    };
  }, [
    isCameraOn,
    isRoomConnected,
    hasOutboundVideo,
    localCameraPublication?.isMuted,
    localMediaStreamTrack,
    onPublisherReady,
  ]);

  // Bind ShowMeFocus to whichever camera is currently publishing. Zoom and
  // autofocus can be exposed by either lens; the torch remains rear-only.
  useEffect(() => {
    const mediaTrack = localParticipant?.getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack || localMediaStreamTrack;
    if (!mediaTrack) {
      if (showMeFocusRef) showMeFocusRef.current = null;
      onShowMeFocusCapabilities?.(EMPTY_FOCUS_CAPABILITIES);
      return undefined;
    }
    bindShowMeFocus(mediaTrack, showMeFocusRef, onShowMeFocusCapabilities);
    return () => {
      if (showMeFocusRef) showMeFocusRef.current = null;
    };
  }, [currentFacingMode, localParticipant, localMediaStreamTrack, showMeFocusRef, onShowMeFocusCapabilities]);

  // Camera control
  useEffect(() => {
    if (localParticipant && isRoomConnected) {
      if (isCameraOn) {
        recoverLiveCamera();
      } else {
        localParticipant.setCameraEnabled(false).catch((error) => {
          console.error('[Camera] disable failed:', error);
        });
      }
    }
  }, [
    isCameraOn,
    isRoomConnected,
    localParticipant,
    recoverLiveCamera,
  ]);

  // Mic control
  useEffect(() => {
    if (!localParticipant || !isRoomConnected) return undefined;
    let cancelled = false;
    localParticipant.setMicrophoneEnabled(
      isMicOn,
      getBroadcastAudioCaptureOptions(audioMode),
      getBroadcastAudioPublishOptions(audioMode),
    ).catch((error) => {
      if (!cancelled) console.error('Error syncing microphone:', error);
    });
    return () => { cancelled = true; };
  }, [audioMode, isRoomConnected, localParticipant, isMicOn]);

  // Install one persistent Web Audio graph on the published microphone. The
  // sliders update AudioParams in-place, so dragging never reacquires the mic
  // or interrupts the live connection.
  useEffect(() => {
    if (!isRoomConnected || !isMicOn || !localMicrophoneTrack?.setProcessor) return undefined;
    let cancelled = false;
    let installedProcessor = null;

    const installProcessor = async () => {
      try {
        const currentProcessor = localMicrophoneTrack.getProcessor?.();
        if (currentProcessor?.name === CREATOR_AUDIO_PROCESSOR_NAME) {
          audioProcessorRef.current = currentProcessor;
          currentProcessor.updateSettings(audioSettingsRef.current);
          return;
        }
        const processor = new CreatorAudioProcessor(audioSettingsRef.current);
        await localMicrophoneTrack.setProcessor(processor, false);
        installedProcessor = processor;
        if (cancelled) {
          if (localMicrophoneTrack.getProcessor?.() === processor) {
            await localMicrophoneTrack.stopProcessor?.();
          }
          return;
        }
        audioProcessorRef.current = processor;
        processor.updateSettings(audioSettingsRef.current);
      } catch (error) {
        console.warn('[Audio] Live equalizer is unavailable on this browser:', error);
      }
    };

    installProcessor();
    return () => {
      cancelled = true;
      if (audioProcessorRef.current === installedProcessor) {
        audioProcessorRef.current = null;
      }
      if (installedProcessor && localMicrophoneTrack.getProcessor?.() === installedProcessor) {
        localMicrophoneTrack.stopProcessor?.().catch(() => {});
      }
    };
  }, [isMicOn, isRoomConnected, localMicrophoneTrack]);

  useEffect(() => {
    audioProcessorRef.current?.updateSettings({ micVolume, balance, treble, bass });
  }, [balance, bass, micVolume, treble]);

  // Speaker volume controls remote call-in audio heard by the creator. The
  // outgoing broadcast microphone remains separate from this monitor level.
  useEffect(() => {
    if (!room) return undefined;
    const { speakerGain } = getCreatorAudioParameters({ speakerVolume });
    const ensurePlayback = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        await room.startAudio?.();
      } catch (error) {
        console.warn('[Audio] Creator playback is waiting for a browser gesture:', error);
      }
    };
    const applyVolume = (participant) => {
      try {
        participant?.setVolume?.(speakerGain, Track.Source.Microphone);
      } catch (error) {
        console.warn('[Audio] Unable to set participant monitor volume:', error);
      }
    };
    room.remoteParticipants?.forEach?.(applyVolume);
    const onParticipantConnected = (participant) => applyVolume(participant);
    const onTrackSubscribed = (_track, publication, participant) => {
      if (publication?.source !== Track.Source.Microphone) return;
      applyVolume(participant);
      ensurePlayback();
    };
    const onPlaybackStatusChanged = () => {
      if (!room.canPlaybackAudio) ensurePlayback();
    };
    const onResume = () => ensurePlayback();
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.AudioPlaybackStatusChanged, onPlaybackStatusChanged);
    room.on(RoomEvent.Reconnected, onResume);
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    ensurePlayback();
    return () => {
      room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      room.off(RoomEvent.AudioPlaybackStatusChanged, onPlaybackStatusChanged);
      room.off(RoomEvent.Reconnected, onResume);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
    };
  }, [room, speakerVolume]);

  // A phone call or browser backgrounding can temporarily mute capture, end a
  // hardware track, suspend Web Audio, or disconnect the room. The room-level
  // recovery lives in ControlPanel; this track-level watchdog keeps the same
  // room and only reacquires camera/microphone hardware when it is unhealthy.
  useEffect(() => {
    if (!isRoomConnected || (!isCameraOn && !isMicOn)) return undefined;
    let cancelled = false;

    const recoverInterruptedMedia = async () => {
      if (
        cancelled
        || document.visibilityState === 'hidden'
        || mediaRecoveryInProgressRef.current
      ) return;

      mediaRecoveryInProgressRef.current = true;
      try {
        await room?.startAudio?.().catch(() => {});
        await audioProcessorRef.current?.resume?.();
        await Promise.allSettled([
          isCameraOn ? recoverLiveCamera() : Promise.resolve(true),
          isMicOn ? recoverLiveMicrophone() : Promise.resolve(true),
        ]);
      } finally {
        mediaRecoveryInProgressRef.current = false;
      }
    };

    const scheduleRecovery = (reason) => {
      if (cancelled) return;
      if (mediaRecoveryTimerRef.current !== null) {
        window.clearTimeout(mediaRecoveryTimerRef.current);
      }
      mediaRecoveryTimerRef.current = window.setTimeout(() => {
        mediaRecoveryTimerRef.current = null;
        recoverInterruptedMedia();
      }, getMediaRecoveryDelay(reason));
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') scheduleRecovery('visibility');
    };
    const onFocus = () => scheduleRecovery('focus');
    const onPageShow = () => scheduleRecovery('pageshow');
    const onOnline = () => scheduleRecovery('online');
    const onTrackEnded = () => scheduleRecovery('ended');
    const onTrackMuted = () => scheduleRecovery('mute');
    const onTrackUnmuted = () => scheduleRecovery('unmute');

    const mediaTracks = [
      isCameraOn ? localMediaStreamTrack : null,
      isMicOn ? localMicrophoneMediaStreamTrack : null,
    ].filter(Boolean);
    mediaTracks.forEach((mediaTrack) => {
      mediaTrack.addEventListener('ended', onTrackEnded);
      mediaTrack.addEventListener('mute', onTrackMuted);
      mediaTrack.addEventListener('unmute', onTrackUnmuted);
    });

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      if (mediaRecoveryTimerRef.current !== null) {
        window.clearTimeout(mediaRecoveryTimerRef.current);
        mediaRecoveryTimerRef.current = null;
      }
      mediaTracks.forEach((mediaTrack) => {
        mediaTrack.removeEventListener('ended', onTrackEnded);
        mediaTrack.removeEventListener('mute', onTrackMuted);
        mediaTrack.removeEventListener('unmute', onTrackUnmuted);
      });
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('online', onOnline);
    };
  }, [
    isCameraOn,
    isMicOn,
    isRoomConnected,
    localMediaStreamTrack,
    localMicrophoneMediaStreamTrack,
    recoverLiveCamera,
    recoverLiveMicrophone,
    room,
  ]);

  // Switch camera function - uses restartTrack with facingMode + resolution constraints
  const switchCamera = useCallback(async () => {
    if (!localParticipant || isSwitching) return;
    
    setIsSwitching(true);
    const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
    console.log('[Camera] Switching from', currentFacingMode, 'to', newFacingMode);
    
    // Build resolution constraints from current quality setting.
    // NOTE: no `min` here — hard minimums throw OverconstrainedError on phones
    // (especially the front camera in portrait), which is why the switch would
    // silently fail. `ideal` lets the browser pick the closest supported size.
    const exactConstraints = getCameraMediaConstraints(videoQuality, orientation, newFacingMode, true);
    const idealConstraints = getCameraMediaConstraints(videoQuality, orientation, newFacingMode, false);
    
    try {
      let switched = false;
      
      // Method 1: restartTrack with exact facingMode + resolution
      try {
        const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
        if (cameraPub && cameraPub.videoTrack) {
          await cameraPub.videoTrack.restartTrack(exactConstraints);
          switched = true;
        }
      } catch (err) {
        console.warn('[Camera] restartTrack exact failed:', err);
      }

      // Method 1b: restartTrack with ideal facingMode + resolution
      if (!switched) {
        try {
          const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
          if (cameraPub && cameraPub.videoTrack) {
            await cameraPub.videoTrack.restartTrack(idealConstraints);
            switched = true;
          }
        } catch (err) {
          console.warn('[Camera] restartTrack ideal failed:', err);
        }
      }
      
      // Method 2: switchActiveDevice then re-apply resolution
      if (!switched) {
        try {
          const devices = await Room.getLocalDevices('videoinput');
          const target = devices.find(d => {
            const label = (d.label || '').toLowerCase();
            if (newFacingMode === 'environment') {
              return label.includes('back') || label.includes('rear') || label.includes('environment');
            }
            return label.includes('front') || label.includes('user') || label.includes('facetime');
          });
          
          if (target && room) {
            await room.switchActiveDevice('videoinput', target.deviceId);
            // Re-apply resolution after device switch
            const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
            if (cameraPub?.videoTrack) {
              await cameraPub.videoTrack.restartTrack(idealConstraints).catch(() => {});
            }
            switched = true;
          }
          
          // Method 3: Pick any other device
          if (!switched) {
            const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
            const currentDeviceId = cameraPub?.track?.mediaStreamTrack?.getSettings()?.deviceId;
            const otherDevice = devices.find(d => d.deviceId !== currentDeviceId && d.deviceId !== 'default');
            if (otherDevice && room) {
              await room.switchActiveDevice('videoinput', otherDevice.deviceId);
              if (cameraPub?.videoTrack) {
                await cameraPub.videoTrack.restartTrack(idealConstraints).catch(() => {});
              }
              switched = true;
            }
          }
        } catch (err2) {
          console.error('[Camera] Fallback methods failed:', err2);
        }
      }
      
      // Method 4: raw getUserMedia + replaceTrack (most reliable on mobile).
      // Try `exact` first (forces the other lens), then fall back to `ideal`.
      if (!switched) {
        try {
          const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
          if (cameraPub?.track) {
            const oldTrack = cameraPub.track.mediaStreamTrack;
            let newStream;
            try {
              newStream = await navigator.mediaDevices.getUserMedia({
                video: exactConstraints,
              });
            } catch {
              newStream = await navigator.mediaDevices.getUserMedia({
                video: idealConstraints,
              });
            }
            const newMediaTrack = newStream.getVideoTracks()[0];
            if (newMediaTrack) {
              await cameraPub.track.replaceTrack(newMediaTrack);
              oldTrack?.stop();
              switched = true;
            }
          }
        } catch (err) {
          console.error('[Camera] getUserMedia switch failed:', err);
        }
      }

      if (switched) {
        const switchedTrack = localParticipant
          .getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack;
        await resetCameraZoom(switchedTrack).catch(() => {});
        setCurrentFacingMode(newFacingMode);
        onFacingModeChange?.(newFacingMode);
        toast.success(`Switched to ${newFacingMode === 'user' ? 'front' : 'back'} camera`);
      } else {
        console.warn('[Camera] No alternate camera found');
        try { await localParticipant.setCameraEnabled(true); } catch {}
        toast.error('Unable to switch camera on this device');
      }
    } finally {
      setIsSwitching(false);
    }
  }, [localParticipant, room, currentFacingMode, isSwitching, videoQuality, orientation, onFacingModeChange]);

  // Expose switch function via ref
  useEffect(() => {
    if (onCameraSwitchTrigger) {
      onCameraSwitchTrigger.current = switchCamera;
      console.log('[Camera] Switch function exposed via ref');
    }
  }, [switchCamera, onCameraSwitchTrigger]);

  return (
    <div className="w-full h-full relative">
      {localVideoTrack ? (
        <>
          <VideoTrack
            trackRef={localVideoTrack}
            aria-hidden
            className="absolute inset-0 h-full w-full scale-110 blur-2xl opacity-60"
            style={{ objectFit: CREATOR_CAMERA_BACKGROUND_FIT }}
          />
          <VideoTrack
            trackRef={localVideoTrack}
            className="relative h-full w-full"
            style={{ objectFit: localCameraFit, objectPosition: 'center' }}
            data-camera-fit={localCameraFit}
          />
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-900">
          <VideoOff className="w-16 h-16 text-gray-600" />
        </div>
      )}
      {isSwitching && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-white text-sm">Switching camera...</p>
          </div>
        </div>
      )}
    </div>
  );
});

// ============================================
// CAMERA PREVIEW (Pre-stream)
// ============================================
const CameraPreview = ({ isCameraOn, facingMode, videoQuality, orientation, showMeFocusRef, onShowMeFocusCapabilities, cameraControlRef }) => {
  const videoRef = useRef(null);
  const backgroundVideoRef = useRef(null);
  const streamRef = useRef(null);
  const [previewFit, setPreviewFit] = useState('contain');

  // Release the pre-live capture synchronously before LiveKit mounts. On iOS,
  // overlapping preview/live requests can reuse a stale cropped camera session.
  const releasePreviewMedia = useCallback(() => {
    const stream = streamRef.current;
    streamRef.current = null;
    releaseCameraPreview(stream, [videoRef.current, backgroundVideoRef.current]);
    if (showMeFocusRef) showMeFocusRef.current = null;
  }, [showMeFocusRef]);

  useEffect(() => {
    if (!cameraControlRef) return undefined;
    cameraControlRef.current = { release: releasePreviewMedia };
    return () => {
      if (cameraControlRef.current?.release === releasePreviewMedia) cameraControlRef.current = null;
    };
  }, [cameraControlRef, releasePreviewMedia]);

  useEffect(() => {
    let cancelled = false;
    if (isCameraOn) {
      const exactConstraints = getCameraMediaConstraints(videoQuality, orientation, facingMode, true);
      const idealConstraints = getCameraMediaConstraints(videoQuality, orientation, facingMode, false);
      const openCamera = async () => {
        try {
          return await navigator.mediaDevices.getUserMedia({ video: exactConstraints, audio: true });
        } catch (exactError) {
          console.info('[Camera] exact startup lens unavailable; using ideal facing mode', exactError);
          return navigator.mediaDevices.getUserMedia({ video: idealConstraints, audio: true });
        }
      };
      openCamera()
        .then(async stream => {
          if (cancelled) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }
          // Mute audio tracks in preview (just securing permission)
          stream.getAudioTracks().forEach(t => t.enabled = false);
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          if (backgroundVideoRef.current) backgroundVideoRef.current.srcObject = stream;
          const videoTrack = stream.getVideoTracks()[0];
          setPreviewFit(getCameraDisplayFit(orientation, videoTrack?.getSettings?.() || {}));
          await resetCameraZoom(videoTrack).catch((error) => {
            console.warn('[Camera] preview zoom reset unavailable:', error);
          });
          await bindShowMeFocus(videoTrack, showMeFocusRef, onShowMeFocusCapabilities);
        })
        .catch(err => console.error('Camera access error:', err));
    } else {
      releasePreviewMedia();
    }

    return () => {
      cancelled = true;
      releasePreviewMedia();
    };
  }, [isCameraOn, facingMode, videoQuality, orientation, showMeFocusRef, onShowMeFocusCapabilities, releasePreviewMedia]);

  if (!isCameraOn) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <VideoOff className="w-20 h-20 text-gray-700" />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      <video
        ref={backgroundVideoRef}
        autoPlay
        playsInline
        muted
        aria-hidden
        className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl opacity-60"
      />
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="relative h-full w-full object-contain"
        style={{ objectFit: previewFit, objectPosition: 'center' }}
        data-camera-fit={previewFit}
      />
    </div>
  );
};

// ============================================
// TRANSPARENT BOTTOM CONTROLS (Max 5% height)
// ============================================
const TransparentControls = React.memo(({
  isCameraOn, toggleCamera,
  isMicOn, toggleMic,
  facingMode, setFacingMode,
  videoQuality, setVideoQuality,
  audioMode, setAudioMode,
  qualityStatus,
  isStreaming, handleStartStream,
  showSettings, setShowSettings,
  speakerVolume, setSpeakerVolume,
  micVolume, setMicVolume,
  balance, setBalance,
  treble, setTreble,
  bass, setBass,
  handleResetAudio,
  onCameraSwitchTrigger,
  showMeFocusRef,
  showMeFocusCapabilities,
  rearLightOn,
  setRearLightOn,
  onShare,
}) => {
  const [showFocusMenu, setShowFocusMenu] = useState(false);
  const [zoom, setZoom] = useState(showMeFocusCapabilities?.zoom || 1);
  const [cameraSwitchPending, setCameraSwitchPending] = useState(false);
  const [streamActionPending, setStreamActionPending] = useState(false);

  useEffect(() => {
    setZoom(showMeFocusCapabilities?.zoom || showMeFocusCapabilities?.zoomMin || 1);
  }, [showMeFocusCapabilities?.zoom, showMeFocusCapabilities?.zoomMin]);

  // Handle camera switch - call the exposed function
  const handleCameraSwitch = useCallback(async () => {
    if (cameraSwitchPending) return;
    setCameraSwitchPending(true);
    console.log('[Controls] Switch camera button pressed');
    try {
      if (facingMode === 'environment' && rearLightOn) {
        try { await showMeFocusRef?.current?.setTorch?.(false); } catch {}
        setRearLightOn(false);
      }
      if (onCameraSwitchTrigger?.current) {
        await onCameraSwitchTrigger.current();
      } else {
        setFacingMode(facingMode === 'user' ? 'environment' : 'user');
      }
      setShowFocusMenu(false);
    } finally {
      setCameraSwitchPending(false);
    }
  }, [cameraSwitchPending, facingMode, onCameraSwitchTrigger, rearLightOn, setFacingMode, setRearLightOn, showMeFocusRef]);

  const handleStreamAction = useCallback(async () => {
    if (streamActionPending) return;
    setStreamActionPending(true);
    try {
      await handleStartStream();
    } finally {
      setStreamActionPending(false);
    }
  }, [handleStartStream, streamActionPending]);

  return (
    <div 
      className="pointer-events-auto absolute inset-x-0 bottom-0 z-40 pt-2"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}
    >
      {showFocusMenu && (
        <CreatorSettingsPortal>
        <div className="fixed bottom-[calc(max(env(safe-area-inset-bottom),0.75rem)+3.5rem)] left-1/2 z-[120] w-[min(22rem,calc(100vw-1rem))] -translate-x-1/2 rounded-2xl border border-blue-400/25 bg-[#080b12]/95 p-4 text-white shadow-2xl backdrop-blur-xl" data-testid="showme-focus-panel" data-creator-front-layer="true">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold">ShowMeFocus</p>
              <p className="text-[11px] text-gray-400">
                {facingMode === 'environment' ? 'Back camera focus, zoom, and light' : 'Front camera focus and zoom'}
              </p>
            </div>
            <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${showMeFocusCapabilities?.autoFocusSupported ? 'bg-green-500/15 text-green-300' : 'bg-gray-700 text-gray-400'}`}>
              {showMeFocusCapabilities?.autoFocusSupported ? 'AUTO FOCUS ON' : 'AUTO FOCUS N/A'}
            </span>
          </div>
          {facingMode === 'environment' && (
            <button
              type="button"
              disabled={!showMeFocusCapabilities?.torchSupported}
              onClick={async () => {
                const nextEnabled = !rearLightOn;
                try {
                  const applied = await showMeFocusRef?.current?.setTorch?.(nextEnabled);
                  if (!applied) return toast.info('Back light is not available on this device or browser');
                  setRearLightOn(nextEnabled);
                } catch {
                  toast.error('Unable to change the back light');
                }
              }}
              className={`mb-3 flex w-full items-center justify-between rounded-lg px-3 py-2 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${rearLightOn ? 'bg-amber-400/20 text-amber-100' : 'bg-white/5 text-gray-300'}`}
              data-testid="showme-back-light"
            >
              <span>Back light</span>
              <span>{rearLightOn ? 'ON' : showMeFocusCapabilities?.torchSupported ? 'OFF' : 'N/A'}</span>
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              const applied = await showMeFocusRef?.current?.autoFocus?.();
              if (applied) toast.success('ShowMeFocus refocused');
              else toast.info('Autofocus is controlled automatically by this camera');
            }}
            className="mb-3 w-full rounded-lg bg-blue-600/20 px-3 py-2 text-xs font-semibold text-blue-200"
            data-testid="showme-focus-auto"
          >
            Refocus automatically
          </button>
          <label className="block text-xs text-gray-300" htmlFor="showme-focus-zoom">
            Manual zoom <span className="float-right font-mono text-blue-300">{Number(zoom).toFixed(1)}x</span>
          </label>
          {showMeFocusCapabilities?.zoomSupported && (
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="showme-focus-zoom-presets">
              {[showMeFocusCapabilities.zoomMin, 1, Math.min(2, showMeFocusCapabilities.zoomMax)]
                .map((value) => Math.min(showMeFocusCapabilities.zoomMax, Math.max(showMeFocusCapabilities.zoomMin, Number(value))))
                .filter((value, index, values) => values.indexOf(value) === index)
                .map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setZoom(value);
                      showMeFocusRef?.current?.setZoom?.(value).catch(() => toast.error('Unable to change camera zoom'));
                    }}
                    className={`rounded-lg border px-2 py-1.5 text-xs font-bold transition ${Math.abs(Number(zoom) - value) < 0.05 ? 'border-amber-300 bg-amber-300/20 text-amber-200' : 'border-white/10 bg-white/5 text-gray-300 hover:bg-white/10'}`}
                  >
                    {Number(value).toFixed(value % 1 === 0 ? 0 : 1)}x
                  </button>
                ))}
            </div>
          )}
          <input
            id="showme-focus-zoom"
            type="range"
            min={showMeFocusCapabilities?.zoomMin || 1}
            max={showMeFocusCapabilities?.zoomMax || 1}
            step={showMeFocusCapabilities?.zoomStep || 0.1}
            value={zoom}
            disabled={!showMeFocusCapabilities?.zoomSupported}
            onChange={(event) => {
              const nextZoom = Number(event.target.value);
              setZoom(nextZoom);
              showMeFocusRef?.current?.setZoom?.(nextZoom).catch(() => toast.error('Unable to change camera zoom'));
            }}
            className="mt-2 w-full accent-blue-500 disabled:opacity-35"
            data-testid="showme-focus-zoom"
          />
          {!showMeFocusCapabilities?.zoomSupported && (
            <p className="mt-2 text-[10px] text-gray-500">Zoom is not exposed for this camera by the current device or browser.</p>
          )}
        </div>
        </CreatorSettingsPortal>
      )}
      <div className="mx-auto flex w-full max-w-3xl items-center justify-end gap-1.5 px-3">
        <button 
          onClick={toggleMic}
          className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-white shadow-xl backdrop-blur-xl transition active:scale-95 ${isMicOn ? 'bg-[#141418]/65 hover:bg-white/15' : 'bg-red-500/30'}`}
          data-testid="mic-toggle"
        >
          <Mic className="w-5 h-5" />
          {!isMicOn && <div className="absolute inset-0 flex items-center justify-center"><div className="w-5 h-0.5 bg-red-500 rotate-45 rounded-full" /></div>}
        </button>

        <button 
          onClick={handleCameraSwitch}
          disabled={cameraSwitchPending}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-[#141418]/65 text-white shadow-xl backdrop-blur-xl transition hover:bg-white/15 active:scale-95 disabled:opacity-60"
          data-testid="camera-switch"
        >
          {cameraSwitchPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <SwitchCamera className="w-5 h-5" />}
        </button>

        <button 
          onClick={toggleCamera}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-white shadow-xl backdrop-blur-xl transition active:scale-95 ${isCameraOn ? 'bg-[#141418]/65 hover:bg-white/15' : 'bg-red-500/30'}`}
          data-testid="camera-toggle"
        >
          {isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5 text-red-300" />}
        </button>

        <button
          onClick={handleStreamAction}
          disabled={streamActionPending}
          className={`h-10 shrink-0 rounded-full px-4 text-sm font-extrabold text-white shadow-lg transition-all ${
            isStreaming 
              ? 'bg-gradient-to-b from-rose-500 to-rose-700 shadow-rose-950/40'
              : 'bg-gradient-to-b from-blue-500 to-blue-700 shadow-blue-950/40'
          } disabled:opacity-70 active:scale-95`}
          data-testid="go-live-btn"
        >
          {streamActionPending ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : isStreaming ? 'End' : 'Go Live'}
        </button>

        <button
          type="button"
          onClick={() => setShowFocusMenu((visible) => !visible)}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-white shadow-xl backdrop-blur-xl transition ${showFocusMenu ? 'border-blue-300/60 bg-blue-500/35' : 'border-blue-400/35 bg-blue-500/25 hover:bg-blue-500/35'}`}
          data-testid="showme-focus-toggle"
          aria-label="ShowMeFocus"
        >
          <Crosshair className="h-5 w-5" />
        </button>

        <div className="relative">
          <button 
            onClick={() => setShowSettings(!showSettings)}
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-white shadow-xl backdrop-blur-xl transition ${showSettings ? 'bg-blue-600/50' : 'bg-[#141418]/65 hover:bg-white/15'}`}
            data-testid="settings-btn"
          >
            <Settings className="w-5 h-5" />
          </button>
          <AudioSettingsDropdown
            isOpen={showSettings} 
            onClose={() => setShowSettings(false)}
            speakerVolume={speakerVolume} 
            setSpeakerVolume={setSpeakerVolume}
            micVolume={micVolume} 
            setMicVolume={setMicVolume}
            balance={balance} 
            setBalance={setBalance}
            treble={treble} 
            setTreble={setTreble}
            bass={bass} 
            setBass={setBass}
            onReset={handleResetAudio}
            videoQuality={videoQuality}
            setVideoQuality={setVideoQuality}
            audioMode={audioMode}
            setAudioMode={setAudioMode}
            settingsLocked={isStreaming}
            qualityStatus={qualityStatus}
          />
        </div>

        <button
          type="button"
          onClick={onShare}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[#141418]/65 text-white shadow-xl backdrop-blur-xl transition hover:bg-white/15"
          data-testid="creator-share-btn"
          aria-label="Share live event"
        >
          <Share2 className="h-5 w-5" />
        </button>

      </div>
    </div>
  );
});

// ============================================
// MAIN CONTROL PANEL COMPONENT
// ============================================
const ControlPanel = ({ user }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [liveSessionProfile, setLiveSessionProfile] = useState(null);
  const deviceOrientation = useDeviceVideoOrientation(liveSessionProfile?.orientation);
  
  // Event state
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventError, setEventError] = useState(null);
  const [roomReady, setRoomReady] = useState(false);
  
  // Stream state
  const [isStreaming, setIsStreaming] = useState(false);
  const liveViewport = useLiveViewportLock({
    active: isStreaming,
    initialDimensions: liveSessionProfile?.viewport,
    allowRotation: true,
  });
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [facingMode, setFacingMode] = useState('user');
  const [videoQuality, setVideoQuality] = useState('auto');
  const [audioMode, setAudioMode] = useState(BROADCAST_AUDIO_MODE_RAW);
  const [broadcastQualityStatus, setBroadcastQualityStatus] = useState({
    connectionQuality: 'pre-live',
    publishingLayer: 'high',
    width: 0,
    height: 0,
    fps: 0,
    bitrateKbps: 0,
  });
  const activeStreamOrientation = liveSessionProfile?.orientation || deviceOrientation;
  const activeStreamVideoQuality = liveSessionProfile?.videoQuality || videoQuality;
  const activeStreamAudioMode = liveSessionProfile?.audioMode || audioMode;
  const [liveKitToken, setLiveKitToken] = useState(null);
  const [liveKitUrl, setLiveKitUrl] = useState(null);
  const [liveKitRoomGeneration, setLiveKitRoomGeneration] = useState(0);
  const liveKitRoomGenerationRef = useRef(0);
  const [viewerCount, setViewerCount] = useState(0);
  const [streamTime, setStreamTime] = useState(0);
  const liveKitRecoveryTimerRef = useRef(null);
  const liveKitRecoveryAttemptRef = useRef(0);
  const liveKitConnectedRef = useRef(false);
  const liveKitConnectWatchdogRef = useRef(null);
  const handlePublisherReady = useCallback(async () => {
    // This is reached only after the creator has an unmuted camera track.
    try {
      const response = await axiosInstance.post(`/livekit/ivs/start/${eventId}`, {
        orientation: activeStreamOrientation,
        video_quality: activeStreamVideoQuality,
      });
      if (!isRelayAnnouncementAccepted(response.data)) {
        console.warn('[AWS IVS] relay is not ready; creator will retry:', {
          state: response.data?.state,
          started: response.data?.started,
        });
        return false;
      }
      toast.success('You are now live!');
      return true;
    } catch (relayError) {
      console.warn('[AWS IVS] creator camera published but live state failed:', relayError);
      return false;
    }
  }, [activeStreamOrientation, activeStreamVideoQuality, eventId]);

  const advanceLiveKitRoomGeneration = useCallback(() => {
    setLiveKitRoomGeneration((current) => {
      const next = current + 1;
      liveKitRoomGenerationRef.current = next;
      return next;
    });
  }, []);

  const cancelLiveKitRecovery = useCallback(() => {
    if (liveKitRecoveryTimerRef.current !== null) {
      window.clearTimeout(liveKitRecoveryTimerRef.current);
      liveKitRecoveryTimerRef.current = null;
    }
  }, []);

  const scheduleLiveKitRecovery = useCallback(() => {
    if (!isStreaming || liveKitRecoveryTimerRef.current !== null) return;

    const attempt = liveKitRecoveryAttemptRef.current;
    const delay = Math.min(700 * Math.pow(2, attempt), 6000);
    liveKitRecoveryTimerRef.current = window.setTimeout(async () => {
      liveKitRecoveryTimerRef.current = null;
      if (!isStreaming || liveKitConnectedRef.current) return;

      try {
        const response = await axiosInstance.post('/livekit/join-as-creator', {
          event_id: eventId,
          is_recovery: true,
        });
        if (!response.data?.token || !response.data?.url) {
          throw new Error('LiveKit recovery returned no room credentials');
        }
        liveKitRecoveryAttemptRef.current = 0;
        setLiveKitToken(response.data.token);
        setLiveKitUrl(response.data.url);
        advanceLiveKitRoomGeneration();
      } catch (recoveryError) {
        liveKitRecoveryAttemptRef.current += 1;
        console.warn('[LiveKit] Basic creator recovery failed:', recoveryError);
        scheduleLiveKitRecovery();
      }
    }, delay);
  }, [advanceLiveKitRoomGeneration, eventId, isStreaming]);

  const cancelLiveKitConnectWatchdog = useCallback(() => {
    if (liveKitConnectWatchdogRef.current !== null) {
      window.clearTimeout(liveKitConnectWatchdogRef.current);
      liveKitConnectWatchdogRef.current = null;
    }
  }, []);

  useEffect(() => {
    cancelLiveKitConnectWatchdog();
    if (!isStreaming || !liveKitToken || !liveKitUrl || liveKitConnectedRef.current) {
      return undefined;
    }
    liveKitConnectWatchdogRef.current = window.setTimeout(() => {
      liveKitConnectWatchdogRef.current = null;
      if (!liveKitConnectedRef.current) {
        console.warn('[LiveKit] Creator room remained disconnected; refreshing credentials');
        scheduleLiveKitRecovery();
      }
    }, 12000);
    return cancelLiveKitConnectWatchdog;
  }, [
    cancelLiveKitConnectWatchdog,
    isStreaming,
    liveKitRoomGeneration,
    liveKitToken,
    liveKitUrl,
    scheduleLiveKitRecovery,
  ]);

  useEffect(() => {
    if (!isStreaming) return undefined;
    const recoverDisconnectedRoom = () => {
      if (
        !liveKitConnectedRef.current
        && navigator.onLine
        && document.visibilityState === 'visible'
      ) {
        scheduleLiveKitRecovery();
      }
    };
    window.addEventListener('online', recoverDisconnectedRoom);
    window.addEventListener('focus', recoverDisconnectedRoom);
    document.addEventListener('visibilitychange', recoverDisconnectedRoom);
    return () => {
      window.removeEventListener('online', recoverDisconnectedRoom);
      window.removeEventListener('focus', recoverDisconnectedRoom);
      document.removeEventListener('visibilitychange', recoverDisconnectedRoom);
    };
  }, [isStreaming, scheduleLiveKitRecovery]);

  useEffect(() => () => {
    cancelLiveKitRecovery();
    cancelLiveKitConnectWatchdog();
  }, [cancelLiveKitConnectWatchdog, cancelLiveKitRecovery]);

  // Audio settings
  // Neutral means no application attenuation. The old 80% defaults made both
  // the creator program mic and viewer call-ins needlessly quiet.
  const [speakerVolume, setSpeakerVolume] = useState(100);
  const [micVolume, setMicVolume] = useState(100);
  const [balance, setBalance] = useState(50);
  const [treble, setTreble] = useState(50);
  const [bass, setBass] = useState(50);
  const [showSettings, setShowSettings] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  
  // Chat/Reactions state
  const [chatConnected, setChatConnected] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [, setMutedChatUserIds] = useState([]);
  const [liveReactions, setLiveReactions] = useState([]);
  const [reactionCount, setReactionCount] = useState(0);
  const [titheDollarEvents, setTitheDollarEvents] = useState([]);
  const animatedTitheIdsRef = useRef(new Set());
  // The running total remains visible. Podcast/Education sender receipts are
  // separate 15-second creator-only notifications; Religion identity stays
  // report-only.
  const [titheTotal, setTitheTotal] = useState(0);
  const {
    notifications: tipNotifications,
    enqueueTipNotification,
  } = useCreatorTipNotifications();
  const [educationQuestions, setEducationQuestions] = useState([]);
  const [educationSpeakers, setEducationSpeakers] = useState([]);
  const [educationSpeakerBusyId, setEducationSpeakerBusyId] = useState(null);
  const [audienceSpeakerToggleBusy, setAudienceSpeakerToggleBusy] = useState(false);
  const chatWsRef = useRef(null);
  const chatConnectingRef = useRef(false);
  // Ids already handled — prevents duplicate messages/reactions/coins/tithes
  // if a reconnect briefly leaves two sockets or the server replays an event.
  const seenIdsRef = useRef(new Set());
  const titheSnapshotReadyRef = useRef(false);
  const messageQueueRef = useRef([]);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  
  // Camera switch callback ref
  const switchCameraRef = useRef(null);
  const previewCameraControlRef = useRef(null);
  const showMeFocusRef = useRef(null);
  const [showMeFocusCapabilities, setShowMeFocusCapabilities] = useState(EMPTY_FOCUS_CAPABILITIES);
  const [rearLightOn, setRearLightOn] = useState(false);

  // The creator receives the same green-dollar count as the sender. Recovery
  // uses the same IDs, so a reconnect cannot replay an already seen tithe.
  const enqueueTitheEffect = useCallback((
    id,
    dollarEffectCount,
    animationStartedAt = null,
    leadingEmojis = [],
  ) => {
    const safeId = String(id || (Date.now() + Math.random()));
    if (animatedTitheIdsRef.current.has(safeId)) return;
    animatedTitheIdsRef.current.add(safeId);
    setTitheDollarEvents((prev) => {
      const effect = {
        id: safeId,
        dollarCount: normalizeShowMeDonationDollarCount(dollarEffectCount),
        leadingEmojis,
        animationStartedAt,
      };
      const existingIndex = prev.findIndex((item) => String(item.id) === safeId);
      if (existingIndex === -1) return [...prev, effect].slice(-100);
      const next = [...prev];
      next[existingIndex] = { ...next[existingIndex], ...effect };
      return next;
    });
  }, []);

  useEffect(() => {
    if (!eventId || event?.chat_enabled === false || (event?.category || "").toLowerCase() === "education") return undefined;
    let active = true;
    axiosInstance.get(`/events/${eventId}/chat`).then((response) => {
      if (!active) return;
      const messages = Array.isArray(response.data?.messages) ? response.data.messages : [];
      const history = messages.map((message) => ({
        id: message.id,
        user_id: message.user_id,
        username: message.user_name || message.username || "Viewer",
        user_picture: message.user_picture,
        message: message.message || "",
        color: "#60a5fa",
        timestamp: message.created_at || message.timestamp,
        sequence: message.sequence,
        is_pinned: Boolean(message.is_pinned),
      }));
      setChatMessages((current) => mergeChatMessages(current, history, 100));
    }).catch((error) => console.warn('[ShowMeCongregation] creator history unavailable:', error));
    return () => { active = false; };
  }, [eventId, event?.category]);

  // Screen protection
  const { isProtected, showWarning, warningMessage, canContinue, violationCount, dismissWarning } = useScreenProtection(eventId);

  // Fetch event data and validate room exists
  useEffect(() => {
    const fetchEventAndInitRoom = async () => {
      setLoading(true);
      setEventError(null);
      
      try {
        // Step 1: Fetch event data
        const response = await axiosInstance.get(`/events/${eventId}`);
        const eventData = response.data;
        setEvent(eventData);

        // A creator can refresh, reopen the control panel, or move between
        // devices while the backend event remains live. Restore that room
        // immediately instead of presenting a disconnected "Go Live" state.
        if (eventData?.status === "live") {
          const roomResponse = await axiosInstance.post('/livekit/join-as-creator', {
            event_id: eventId,
            is_recovery: true,
          });
          if (!roomResponse.data?.token || !roomResponse.data?.url) {
            throw new Error('Live creator session returned no room credentials');
          }
          liveKitConnectedRef.current = false;
          setLiveKitToken(roomResponse.data.token);
          setLiveKitUrl(roomResponse.data.url);
          setLiveSessionProfile(captureLiveSessionProfile(
            readDeviceVideoOrientation(),
            videoQuality,
            audioMode,
          ));
          advanceLiveKitRoomGeneration();
          setIsStreaming(true);
        }
        
        // Step 2: Verify or create the LiveKit room
        // The room will be created when we call /streaming/{eventId}/start
        // For now, just mark as ready since the event exists
        setRoomReady(true);
        
      } catch (error) {
        console.error('Event fetch error:', error);
        
        if (error.response?.status === 404) {
          setEventError('Event not found. Please check the event ID.');
        } else if (error.response?.status === 401) {
          setEventError('Please log in to access this event.');
        } else {
          setEventError(error.response?.data?.detail || 'Failed to load event');
        }
        
        // Don't navigate away immediately - show error on page
      } finally {
        setLoading(false);
      }
    };
    
    if (eventId) {
      fetchEventAndInitRoom();
    }
  }, [advanceLiveKitRoomGeneration, eventId]);

  // Stream timer
  useEffect(() => {
    let interval;
    if (isStreaming) {
      interval = setInterval(() => {
        setStreamTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isStreaming]);

  // WebSocket connection for chat/reactions
  const connectChatWebSocket = useCallback(async () => {
    // Skip if we already have a live or in-flight socket, and never leave a
    // stale one behind — this is what stops duplicate listeners after a
    // reconnect or a fast refresh.
    const existing = chatWsRef.current;
    if (
      chatConnectingRef.current
      || (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING))
    ) return;
    if (reconnectTimeoutRef.current) { clearTimeout(reconnectTimeoutRef.current); reconnectTimeoutRef.current = null; }
    chatConnectingRef.current = true;
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    let creatorTicket = "";
    try {
      const ticketResponse = await axiosInstance.post('/auth/websocket-ticket', {
        event_id: eventId,
      });
      creatorTicket = ticketResponse.data?.ticket || "";
      if (!creatorTicket) throw new Error("WebSocket ticket response was empty");
    } catch (ticketError) {
      chatConnectingRef.current = false;
      console.warn('[CREATOR-DIAG] ticket ERROR', { eventId, error: ticketError });
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = setTimeout(connectChatWebSocket, 3000);
      return;
    }
    const creatorAuth = `&ticket=${encodeURIComponent(creatorTicket)}`;
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/chat/${eventId}?role=creator${creatorAuth}`;
    console.log('[CREATOR-DIAG] connecting', { eventId, role: 'creator' });
    
    try {
      const ws = new WebSocket(wsUrl);
      chatWsRef.current = ws;
      
      ws.onopen = () => {
        chatConnectingRef.current = false;
        console.log('[CREATOR-DIAG] socket OPEN', { eventId });
        setChatConnected(true);
        // Process queued messages
        while (messageQueueRef.current.length > 0) {
          const msg = messageQueueRef.current.shift();
          ws.send(JSON.stringify(msg));
        }
        // Start ping
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 25000);
      };
      
      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        console.log('[CREATOR-DIAG] raw frame', { eventId, raw: event.data });
        try {
          const data = JSON.parse(event.data);
          console.log('[CREATOR-DIAG] decoded', { type: data.type, id: data.id, username: data.username, emoji: data.emoji });
          const eid = data.id ?? data.message?.id;
          // Dedupe per event TYPE+id so a reconnect/replay can't double-count.
          const dedupeKey = eid != null ? `${data.type}:${eid}` : null;
          if (dedupeKey != null) {
            if (seenIdsRef.current.has(dedupeKey)) return;
            seenIdsRef.current.add(dedupeKey);
            if (seenIdsRef.current.size > 500) {
              seenIdsRef.current = new Set([...seenIdsRef.current].slice(-250));
            }
          }
          if (data.type === 'viewer_count' && typeof data.count === 'number') {
            setViewerCount(data.count);
          } else if (data.type === 'message' && data.username) {
            const id = eid ?? (Date.now() + Math.random());
            setChatMessages(prev => mergeChatMessages(prev, [{ ...data, id }], 100));
            console.log('[CREATOR-DIAG] chat state updated', { id, message: data.message });
          } else if ((data.type === 'new_message' || data.type === 'announcement') && data.message) {
            const msg = data.message;
            const id = msg.id || eid || (Date.now() + Math.random());
            setChatMessages(prev => mergeChatMessages(prev, [{
              id,
              user_id: msg.user_id,
              username: msg.user_name || msg.username || "Viewer",
              user_picture: msg.user_picture,
              message: msg.message || "",
              color: "#60a5fa",
              timestamp: msg.created_at || data.timestamp,
              sequence: msg.sequence,
              is_pinned: Boolean(msg.is_pinned),
            }], 100));
          } else if (data.type === 'message_pinned' && data.message_id) {
            setChatMessages((current) => current.map((message) => (
              String(message.id) === String(data.message_id)
                ? { ...message, is_pinned: Boolean(data.is_pinned) }
                : data.is_pinned ? { ...message, is_pinned: false } : message
            )));
          } else if (data.type === 'message_hidden' && data.message_id) {
            setChatMessages((current) => current.filter(
              (message) => String(message.id) !== String(data.message_id),
            ));
          } else if (data.type === 'reaction' && (data.emoji || data.reaction_type)) {
            const id = eid ?? (Date.now() + Math.random());
            const emoji = data.emoji || REACTION_TYPE_EMOJIS[data.reaction_type] || "\u2764\ufe0f";
            const count = Math.min(Math.max(Number(data.count) || 1, 1), 50);
            const visibleBurst = Array.from(
              { length: Math.min(count, 5) },
              (_, index) => ({ id: `${id}:${index}`, emoji }),
            );
            setLiveReactions(prev => [...prev.slice(-30), ...visibleBurst].slice(-31));
            setReactionCount(prev => prev + count);
            console.log('[CREATOR-DIAG] reaction state updated', { id, emoji });
          } else if (data.type === 'tithe_dollar_effect') {
            enqueueTitheEffect(
              eid,
              data.dollar_effect_count,
              data.animation_started_at,
              data.leading_emojis,
            );
            if (typeof data.tithe_total === 'number') setTitheTotal(data.tithe_total);
            if (data.sender_name) {
              enqueueTipNotification({
                id: eid,
                senderName: data.sender_name,
                amount: data.amount,
                receivedAt: Date.now(),
              });
            }
          } else if ((data.type === 'education_private_message' || data.type === 'education_question_waiting') && data.question?.id) {
            setEducationQuestions((current) => (
              current.some((question) => String(question.id) === String(data.question.id))
                ? current
                : [...current, data.question]
            ));
          } else if (data.type === 'education_private_message_pinned' && data.message_id) {
            setEducationQuestions((current) => current.map((question) => (
              String(question.id) === String(data.message_id)
                ? { ...question, is_pinned: Boolean(data.is_pinned) }
                : data.is_pinned ? { ...question, is_pinned: false } : question
            )));
          } else if (data.type === 'education_speaker_updated' && data.speaker?.id) {
            setEducationSpeakers((current) => {
              const withoutSpeaker = current.filter(
                (speaker) => String(speaker.id) !== String(data.speaker.id),
              );
              return ["waiting", "invited", "speaking", "muted"].includes(data.speaker.status)
                ? [...withoutSpeaker, data.speaker]
                : withoutSpeaker;
            });
          } else if (data.type === 'audience_speaker_availability' && typeof data.enabled === 'boolean') {
            setEvent((current) => current ? { ...current, audience_speaker_enabled: data.enabled } : current);
            if (!data.enabled) setEducationSpeakers([]);
          } else if (data.type === 'tithe_total_updated' && typeof data.total_amount === 'number') {
            setTitheTotal(data.total_amount);
          }
        } catch (e) {}
      };
      
      ws.onclose = (e) => {
        chatConnectingRef.current = false;
        console.log('[CREATOR-DIAG] socket CLOSE', { eventId, code: e.code, reason: e.reason });
        setChatConnected(false);
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        // Intentional close (unmount/cleanup) — do NOT reconnect, or a zombie
        // socket keeps competing with the live one and chat silently breaks.
        if (e.code === 1000) return;
        // Reconnect after delay — clear any prior timer so only one is ever queued.
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(connectChatWebSocket, 3000);
      };
      
      ws.onerror = () => {
        chatConnectingRef.current = false;
        console.log('[CREATOR-DIAG] socket ERROR', { eventId });
        ws.close();
      };
    } catch (error) {
      chatConnectingRef.current = false;
      console.error('WebSocket error:', error);
    }
  }, [eventId, enqueueTitheEffect, enqueueTipNotification]);

  // WebSockets provide the fastest updates. This snapshot is the recovery path
  // for a creator who opens the panel after the event is live or whose socket
  // reconnects while chat, reactions, or donations are arriving.
  useEffect(() => {
    if (!eventId || !event) return undefined;
    let cancelled = false;
    let snapshotInFlight = false;

    const fetchCreatorSnapshot = async () => {
      if (snapshotInFlight) return;
      snapshotInFlight = true;
      try {
      const snapshotCategory = (event?.category || "").trim().toLowerCase();
      const education = snapshotCategory === "education";
      const audienceSpeaker = (
        (education || snapshotCategory === "podcast" || snapshotCategory === "religion")
        && event?.audience_speaker_enabled !== false
      );
      const [eventRes, chatRes, reactionRes, titheRes, questionRes, speakerRes, muteRes] = await Promise.allSettled([
        axiosInstance.get(`/events/${eventId}`),
        event.chat_enabled !== false && !education
          ? axiosInstance.get(`/events/${eventId}/chat?limit=25`)
          : Promise.resolve(null),
        event.reactions_enabled !== false
          ? axiosInstance.get(`/events/${eventId}/reactions/recent?limit=25`)
          : Promise.resolve(null),
        axiosInstance.get(`/events/${eventId}/tithes/effects?limit=50`),
        education && event.chat_enabled !== false
          ? axiosInstance.get(`/events/${eventId}/questions?limit=100`)
          : Promise.resolve(null),
        audienceSpeaker
          ? axiosInstance.get(`/events/${eventId}/speaker-hands`)
          : Promise.resolve(null),
        event.chat_enabled !== false
          ? axiosInstance.get(`/events/${eventId}/chat-mutes`)
          : Promise.resolve(null),
      ]);
      if (cancelled) return;

      const nextMutedUserIds = muteRes.status === 'fulfilled' && Array.isArray(muteRes.value?.data?.muted_user_ids)
        ? muteRes.value.data.muted_user_ids.map(String)
        : [];
      const nextMutedUserSet = new Set(nextMutedUserIds);

      if (eventRes.status === 'fulfilled' && eventRes.value?.data) {
        setEvent((current) => reuseEqualSnapshot(current, eventRes.value.data));
      }

      if (chatRes.status === 'fulfilled' && Array.isArray(chatRes.value?.data?.messages)) {
        const snapshot = chatRes.value.data.messages.slice(-25).map((msg) => ({
          id: msg.id,
          user_id: msg.user_id,
          username: msg.user_name || msg.username || "Viewer",
          user_picture: msg.user_picture,
          message: msg.message || "",
          color: "#60a5fa",
          timestamp: msg.created_at,
          sequence: msg.sequence,
          is_pinned: Boolean(msg.is_pinned),
          is_chat_muted: nextMutedUserSet.has(String(msg.user_id || "")),
        }));
        setChatMessages((current) => mergeChatMessages(current, snapshot, 25));
      }

      if (reactionRes.status === 'fulfilled' && Array.isArray(reactionRes.value?.data?.reactions)) {
        const fresh = [];
        reactionRes.value.data.reactions.forEach((reaction) => {
          const id = reaction.id || `${reaction.reaction_type}:${reaction.created_at}`;
          const key = `reaction:${id}`;
          if (seenIdsRef.current.has(key)) return;
          seenIdsRef.current.add(key);
          fresh.push({
            id,
            emoji: reaction.emoji || REACTION_TYPE_EMOJIS[reaction.reaction_type] || "\u2764\ufe0f",
          });
        });
        if (fresh.length) {
          setLiveReactions(prev => [...prev.slice(-30), ...fresh].slice(-30));
          setReactionCount(prev => prev + fresh.length);
        }
      }

      if (titheRes.status === 'fulfilled' && titheRes.value?.data) {
        const data = titheRes.value.data;
        if (typeof data.tithe_total === 'number') setTitheTotal(data.tithe_total);
        const rows = Array.isArray(data.dollar_effects) ? [...data.dollar_effects].reverse() : [];
        rows.forEach((row) => {
          const numericStartedAt = Number(row.animation_started_at);
          const createdAt = Date.parse(row.created_at || "");
          const receivedAt = Number.isFinite(numericStartedAt) ? numericStartedAt : createdAt;
          if (row.sender_name && Number.isFinite(receivedAt)) {
            enqueueTipNotification({
              id: row.id,
              senderName: row.sender_name,
              amount: row.amount,
              receivedAt,
            });
          }
        });
        if (!titheSnapshotReadyRef.current) {
          // The first recovery snapshot can race the WebSocket immediately
          // after a donation. Do not silently mark a just-started animation as
          // old: enqueue recent rows while still seeding older history.
          const now = Date.now();
          rows.forEach((row) => {
            seenIdsRef.current.add(`tithe:${row.id}`);
            const numericStartedAt = Number(row.animation_started_at);
            const createdAt = Date.parse(row.created_at || "");
            const effectStartedAt = Number.isFinite(numericStartedAt)
              ? numericStartedAt
              : createdAt;
            if (Number.isFinite(effectStartedAt) && Math.abs(now - effectStartedAt) <= 30000) {
              enqueueTitheEffect(
                row.id,
                row.dollar_effect_count,
                row.animation_started_at,
                row.leading_emojis,
              );
            } else {
              // Seed history without playing it. Otherwise the second poll
              // queues up to 50 old effects ahead of the next live donation.
              animatedTitheIdsRef.current.add(String(row.id));
            }
          });
          titheSnapshotReadyRef.current = true;
        } else {
          rows.forEach((row) => {
            enqueueTitheEffect(
              row.id,
              row.dollar_effect_count,
              row.animation_started_at,
              row.leading_emojis,
            );
          });
        }
      }

      if (questionRes.status === 'fulfilled' && Array.isArray(questionRes.value?.data?.questions)) {
        const nextQuestions = questionRes.value.data.questions.map((question) => ({
          ...question,
          is_chat_muted: nextMutedUserSet.has(String(question.user_id || "")),
        }));
        setEducationQuestions((current) => reuseEqualSnapshot(current, nextQuestions));
      }
      if (speakerRes.status === 'fulfilled' && Array.isArray(speakerRes.value?.data?.speakers)) {
        setEducationSpeakers((current) => reuseEqualSnapshot(current, speakerRes.value.data.speakers));
      }
      } finally {
        snapshotInFlight = false;
      }
    };

    fetchCreatorSnapshot().catch(() => {});
    // WebSocket delivery is the low-latency path. A short recovery poll ensures
    // reactions also reach creators connected to another serverless instance.
    const interval = window.setInterval(
      () => fetchCreatorSnapshot().catch(() => {}),
      chatConnected ? 8000 : 3000
    );
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      titheSnapshotReadyRef.current = false;
    };
  }, [eventId, event?.id, event?.chat_enabled, event?.reactions_enabled, event?.audience_speaker_enabled, enqueueTitheEffect, enqueueTipNotification, chatConnected]);

  // Connect WebSocket when event loads
  useEffect(() => {
    // Default-on when the flags are absent — mirrors the viewer (LiveStreamViewer)
    // and the backend, which both treat missing chat_enabled/reactions_enabled as
    // enabled. The old truthy check (event.chat_enabled || event.reactions_enabled)
    // left the creator's socket unopened for any event doc missing these fields,
    // so the creator never received viewer messages or reaction emojis.
    if (event) {
      console.log('[CREATOR-DIAG] connect gate PASSED', { eventId, chat_enabled: event?.chat_enabled, reactions_enabled: event?.reactions_enabled });
      connectChatWebSocket();
    } else {
      console.log('[CREATOR-DIAG] connect gate SKIPPED', { eventId, hasEvent: !!event, chat_enabled: event?.chat_enabled, reactions_enabled: event?.reactions_enabled });
    }
    
    return () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (chatWsRef.current) {
        chatWsRef.current.onclose = null;
        chatWsRef.current.close(1000);
        chatWsRef.current = null;
      }
      chatConnectingRef.current = false;
    };
  }, [eventId, event?.id, event?.chat_enabled, event?.reactions_enabled, connectChatWebSocket]);

  // Toggle camera
  const toggleCamera = useCallback(() => {
    setIsCameraOn(prev => !prev);
  }, []);

  // Toggle mic
  const toggleMic = useCallback(() => {
    setIsMicOn(prev => !prev);
  }, []);

  const handleOpenShareSheet = useCallback(() => {
    setShowShareSheet(true);
  }, []);

  // Reset audio settings
  const handleResetAudio = useCallback(() => {
    setSpeakerVolume(100);
    setMicVolume(100);
    setBalance(50);
    setTreble(50);
    setBass(50);
  }, []);

  // Start/End stream
  const handleStartStream = useCallback(async () => {
    if (isStreaming) {
      // Tear down locally before awaiting remote services. Deleting the
      // LiveKit room fires onDisconnected; leaving isStreaming true during
      // that request allowed the recovery loop to reconnect an ended stream.
      cancelLiveKitRecovery();
      cancelLiveKitConnectWatchdog();
      liveKitRecoveryAttemptRef.current = 0;
      liveKitConnectedRef.current = false;
      setIsStreaming(false);
      setLiveSessionProfile(null);
      setLiveKitToken(null);
      setLiveKitUrl(null);
      setStreamTime(0);
      try {
        await axiosInstance.post(`/livekit/end-stream/${eventId}`);
        toast.success('Stream ended');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Stream closed locally; remote shutdown is still being finalized');
      }
    } else {
      // Start stream - join as creator via LiveKit
      const frozenSessionProfile = captureLiveSessionProfile(
        deviceOrientation,
        videoQuality,
        audioMode,
      );
      setLiveSessionProfile(frozenSessionProfile);
      try {
        const response = await axiosInstance.post(`/livekit/join-as-creator`, {
          event_id: eventId
        });

        // Stop the preview completely before mounting LiveKit. Reusing Safari's
        // preview MediaStreamTrack could publish a track that looked live to the
        // sender but decoded as black for viewers. A creator refresh worked
        // because it always acquired a fresh camera track; first Go Live now
        // deliberately uses that same path.
        previewCameraControlRef.current?.release?.();
        await new Promise((resolve) => window.setTimeout(resolve, 120));

        liveKitConnectedRef.current = false;
        setLiveKitToken(response.data.token);
        setLiveKitUrl(response.data.url);
        advanceLiveKitRoomGeneration();
        setIsStreaming(true);
        setRoomReady(true);
        toast.info('Connecting your live stream…');
      } catch (error) {
        setLiveSessionProfile(null);
        const errorMsg = error.response?.data?.detail || 'Failed to start stream';
        toast.error(errorMsg);
        // If room creation failed, show error but don't replace the whole page
        if (error.response?.status === 404) {
          toast.error('Event not found. Please check the event ID.');
        } else if (error.response?.status === 403) {
          toast.error('You are not authorized to stream this event.');
        }
      }
    }
  }, [advanceLiveKitRoomGeneration, audioMode, cancelLiveKitConnectWatchdog, cancelLiveKitRecovery, deviceOrientation, isStreaming, eventId, videoQuality]);

  const handleVideoQualityChange = useCallback((nextQuality) => {
    if (liveSessionProfile) {
      toast.info('Video quality is locked until this live stream ends.');
      return;
    }
    setVideoQuality(nextQuality);
  }, [liveSessionProfile]);

  const handleAudioModeChange = useCallback((nextMode) => {
    if (liveSessionProfile) {
      toast.info('Audio profile is locked until this live stream ends.');
      return;
    }
    setAudioMode(nextMode);
  }, [liveSessionProfile]);

  const handleCreatorExit = useCallback(async () => {
    if (isStreaming) {
      const shouldExit = window.confirm('Exit Creator Studio and end this live stream?');
      if (!shouldExit) return;
      await handleStartStream();
    }
    navigate('/creator/dashboard');
  }, [handleStartStream, isStreaming, navigate]);

  const requestStartStream = handleStartStream;
  // Legacy prompt markup remains inert for old cached event data; orientation
  // now follows the device and never requires creator confirmation.
  const showOrientationPrompt = false;
  const confirmOrientationAndGoLive = handleStartStream;
  const setShowOrientationPrompt = () => {};

  const handlePinMessage = async (message) => {
    if (!message?.id) return;
    const shouldPin = !message.is_pinned;
    const previousMessages = chatMessages;
    setChatMessages((current) => current.map((item) => (
      String(item.id) === String(message.id)
        ? { ...item, is_pinned: shouldPin }
        : shouldPin ? { ...item, is_pinned: false } : item
    )));
    try {
      await axiosInstance.post(`/events/${eventId}/chat/${message.id}/pin`, null, { params: { is_pinned: shouldPin } });
    } catch (error) {
      setChatMessages(previousMessages);
      toast.error(error.response?.data?.detail || `Unable to ${shouldPin ? "pin" : "unpin"} comment`);
    }
  };

  const handleMuteChatUser = async (message) => {
    if (!message?.user_id) return;
    try {
      await axiosInstance.post(`/events/${eventId}/chat-mutes/${message.user_id}`);
      const mutedUserId = String(message.user_id);
      setChatMessages((current) => current.map((item) => (
        String(item.user_id) === mutedUserId ? { ...item, is_chat_muted: true } : item
      )));
      setEducationQuestions((current) => current.map((item) => (
        String(item.user_id) === mutedUserId ? { ...item, is_chat_muted: true } : item
      )));
      toast.success(`${message.username || message.user_name || "Viewer"} can no longer chat in this event.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to mute this viewer's chat");
    }
  };

  const handleUnmuteChatUser = async (message) => {
    if (!message?.user_id) return;
    try {
      await axiosInstance.delete(`/events/${eventId}/chat-mutes/${message.user_id}`);
      const unmutedUserId = String(message.user_id);
      setChatMessages((current) => current.map((item) => (
        String(item.user_id) === unmutedUserId ? { ...item, is_chat_muted: false } : item
      )));
      setEducationQuestions((current) => current.map((item) => (
        String(item.user_id) === unmutedUserId ? { ...item, is_chat_muted: false } : item
      )));
      toast.success(`${message.username || message.user_name || "Viewer"} can chat again.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to unmute this viewer's chat");
    }
  };

  const handlePinEducationQuestion = async (question) => {
    if (!question?.id) return;
    const shouldPin = !question.is_pinned;
    const previousQuestions = educationQuestions;
    setEducationQuestions((current) => current.map((item) => (
      String(item.id) === String(question.id)
        ? { ...item, is_pinned: shouldPin }
        : shouldPin ? { ...item, is_pinned: false } : item
    )));
    try {
      await axiosInstance.post(`/events/${eventId}/chat/${question.id}/pin`, null, { params: { is_pinned: shouldPin } });
    } catch (error) {
      setEducationQuestions(previousQuestions);
      toast.error(error.response?.data?.detail || `Unable to ${shouldPin ? "pin" : "unpin"} this private message`);
    }
  };

  const deleteEducationQuestion = async (question) => {
    try {
      await axiosInstance.delete(`/events/${eventId}/questions/${question.id}`);
      setEducationQuestions((current) => current.filter((item) => String(item.id) !== String(question.id)));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete this question");
    }
  };

  const updateEducationSpeaker = (speaker) => {
    if (!speaker?.id) return;
    setEducationSpeakers((current) => {
      const remaining = current.filter((item) => String(item.id) !== String(speaker.id));
      return ["waiting", "invited", "speaking", "muted"].includes(speaker.status)
        ? [...remaining, speaker]
        : remaining;
    });
  };

  const inviteEducationSpeaker = async (speaker) => {
    setEducationSpeakerBusyId(speaker.id);
    try {
      const { data } = await axiosInstance.post(`/events/${eventId}/speaker-hands/${speaker.id}/invite`);
      updateEducationSpeaker(data?.speaker);
      toast.success(`${speaker.user_name || "Viewer"} can turn on their microphone.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to enable this viewer's microphone");
    } finally {
      setEducationSpeakerBusyId(null);
    }
  };

  const muteEducationSpeaker = async (speaker) => {
    setEducationSpeakerBusyId(speaker.id);
    try {
      const { data } = await axiosInstance.post(`/events/${eventId}/speaker-hands/${speaker.id}/mute`);
      updateEducationSpeaker(data?.speaker);
      toast.success(`${speaker.user_name || "Viewer"}'s microphone is off.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to mute this viewer");
    } finally {
      setEducationSpeakerBusyId(null);
    }
  };

  const endEducationSpeaker = async (speaker) => {
    setEducationSpeakerBusyId(speaker.id);
    try {
      const { data } = await axiosInstance.post(`/events/${eventId}/speaker-hands/${speaker.id}/end`);
      updateEducationSpeaker(data?.speaker);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to end speaker access");
    } finally {
      setEducationSpeakerBusyId(null);
    }
  };

  const toggleAudienceSpeakerAvailability = async () => {
    if (audienceSpeakerToggleBusy) return;
    const previousEnabled = event?.audience_speaker_enabled !== false;
    const nextEnabled = !previousEnabled;
    setAudienceSpeakerToggleBusy(true);
    setEvent((current) => current ? { ...current, audience_speaker_enabled: nextEnabled } : current);
    try {
      const { data } = await axiosInstance.put(`/events/${eventId}/audience-speaker`, {
        enabled: nextEnabled,
      });
      const enabled = Boolean(data?.audience_speaker_enabled);
      setEvent((current) => current ? { ...current, audience_speaker_enabled: enabled } : current);
      if (!enabled) setEducationSpeakers([]);
      const featureCategory = (event?.category || "").trim().toLowerCase();
      const featureName = featureCategory === "podcast"
        ? "Call-In"
        : featureCategory === "religion" ? "Mic" : "Raise Hand";
      toast.success(`${featureName} is ${enabled ? "on" : "off"}.`);
    } catch (error) {
      setEvent((current) => current ? { ...current, audience_speaker_enabled: previousEnabled } : current);
      toast.error(error.response?.data?.detail || "Unable to change audience microphone access");
    } finally {
      setAudienceSpeakerToggleBusy(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Loading event...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (eventError || !event) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-black">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-white text-xl font-bold mb-2">
            {eventError || 'Event Not Found'}
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Unable to load the event. Please check the event ID and try again.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/creator')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const creatorEventIsLive = isStreaming || event?.status === "live";
  const showOverlays = creatorEventIsLive && (event?.chat_enabled !== false || event?.reactions_enabled !== false);
  const categoryKey = (event?.category || "").trim().toLowerCase();
  const isEducation = categoryKey === "education";
  const isPodcast = categoryKey === "podcast";
  const isReligion = categoryKey === "religion";
  const isAudienceSpeakerEvent = (
    (isEducation || isPodcast || isReligion)
    && event?.audience_speaker_enabled !== false
  );
  const isShowMeExperience = isVisibleEventCategory(event?.category);
  const experience = getEventExperience(event?.category);

  return (
    <div 
      className={`${isStreaming ? "live-viewport-lock" : "relative min-h-[320px]"} bg-black overflow-hidden`}
      style={{
        height: isStreaming ? `${liveViewport.height}px` : '100svh',
        width: isStreaming ? `${liveViewport.width}px` : '100%',
      }}
      data-testid="control-panel"
      data-live-viewport-locked={isStreaming ? "true" : "false"}
    >
      {/* Screen Protection */}
      <ScreenProtectionOverlay
        showWarning={showWarning}
        warningMessage={warningMessage}
        canContinue={canContinue}
        violationCount={violationCount}
        onDismiss={dismissWarning}
        isProtected={false}
      />

      {/* Go-live orientation hold prompt (creator-only, one-time before first go-live) */}
      {showOrientationPrompt && !isStreaming && (() => {
        const isPortrait = true;
        return (
          <div className="absolute inset-0 z-[100] flex flex-col items-center justify-center bg-black/92 backdrop-blur-sm px-8 text-center" data-testid="creator-orientation-prompt">
            <style>{`@keyframes smlHoldPortrait{0%,8%{transform:rotate(-16deg)}46%,88%{transform:rotate(0)}100%{transform:rotate(-16deg)}}@keyframes smlHoldLandscape{0%,8%{transform:rotate(0)}46%,88%{transform:rotate(-90deg)}100%{transform:rotate(0)}}`}</style>
            <p className="text-blue-400 font-mono text-xs tracking-[0.18em] mb-3">{isPortrait ? "SHOWMEPORTRAITMODE" : "SHOWMELANDSCAPEMODE"}</p>
            <h2 className="text-white text-3xl font-black mb-3">{isPortrait ? "Hold your phone upright" : "Turn your phone sideways"}</h2>
            <p className="text-gray-300 max-w-xs mb-10">{isPortrait ? "This event streams vertically. Keep your phone in portrait for the whole broadcast." : "This event streams widescreen. Rotate your phone to landscape for the whole broadcast."}</p>
            <div className="relative w-40 h-40 flex items-center justify-center mb-12">
              <div className="absolute w-32 h-32 rounded-full border-2 border-dashed border-blue-400/30" />
              <div
                className="w-16 h-28 rounded-xl border-2 border-blue-400 bg-blue-500/10 flex items-center justify-center"
                style={{ animation: `${isPortrait ? "smlHoldPortrait" : "smlHoldLandscape"} 2.6s ease-in-out infinite` }}
              >
                <span className="w-8 h-1.5 rounded-full bg-blue-300/50" />
              </div>
            </div>
            <button
              onClick={confirmOrientationAndGoLive}
              className="w-full max-w-xs py-4 rounded-2xl bg-gradient-to-b from-blue-500 to-blue-600 text-white text-lg font-black hover:from-blue-400 hover:to-blue-500 transition-colors"
              data-testid="orientation-confirm-btn"
            >
              OK, I'm ready — Go Live
            </button>
            <button
              onClick={() => setShowOrientationPrompt(false)}
              className="mt-4 text-gray-400 text-sm hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        );
      })()}

      {/* Full Screen Video */}
      <div className="absolute inset-0">
        <ProtectedContent isProtected={isProtected} showWarning={showWarning} className="live-video-stage w-full h-full">
          {isStreaming && liveKitToken && liveKitUrl ? (
            /* StreamPublisher publishes the preserved pre-live track after
               LiveKit connects, so automatic capture stays off here. */
            <ManualCreatorRoom
              key={`basic-creator-${liveKitRoomGeneration}-${liveKitToken}`}
              serverUrl={liveKitUrl}
              token={liveKitToken}
              connectOptions={{
                // All iPhone browsers use WebKit. On Wi-Fi networks that block
                // direct ICE/UDP, skip repeated regional failures and use the
                // LiveKit Cloud TURN relay advertised over TLS.
                rtcConfig: IS_IOS_WEBKIT
                  ? { iceTransportPolicy: 'relay' }
                  : undefined,
                peerConnectionTimeout: 30000,
              }}
              onConnected={() => {
                if (liveKitRoomGeneration !== liveKitRoomGenerationRef.current) return;
                liveKitConnectedRef.current = true;
                liveKitRecoveryAttemptRef.current = 0;
                cancelLiveKitRecovery();
                cancelLiveKitConnectWatchdog();
                toast.info('Publishing your camera…');
              }}
              onDisconnected={() => {
                if (liveKitRoomGeneration !== liveKitRoomGenerationRef.current) return;
                liveKitConnectedRef.current = false;
                scheduleLiveKitRecovery();
              }}
              onError={(roomError) => {
                if (liveKitRoomGeneration !== liveKitRoomGenerationRef.current) return;
                liveKitConnectedRef.current = false;
                console.warn('[LiveKit] Basic creator room error:', roomError);
                scheduleLiveKitRecovery();
              }}
              options={{
                adaptiveStream: true,
                dynacast: true,
                // Route remote call-in audio through Web Audio so the speaker
                // amplifier can apply gain above the HTML media limit.
                webAudioMix: true,
                disconnectOnPageLeave: false,
                // Preserve the captured camera across an in-place room
                // recovery so the creator never has to restart the stream.
                stopLocalTrackOnUnpublish: false,
                // --- Low-latency, high-quality publishing ---
                // Simulcast lets the SFU hand each viewer the best layer it can
                // sustain, so weak viewers get a lower layer instead of buffering
                // the whole stream — the source resolution/fps/bitrate stay high.
                publishDefaults: {
                  simulcast: true,
                  videoCodec: 'h264',
                  backupCodec: false,
                  ...getBroadcastAudioPublishOptions(activeStreamAudioMode),
                  degradationPreference: 'balanced',
                  videoEncoding: getBroadcastVideoEncoding(activeStreamVideoQuality),
                },
                audioCaptureDefaults: getBroadcastAudioCaptureOptions(activeStreamAudioMode),
                reconnectPolicy: {
                  nextRetryDelayInMs: (ctx) => Math.min(1000 * Math.pow(2, ctx.retryCount), 10000),
                  maxRetries: 20
                }
              }}
            >
              <StreamPublisher 
                isCameraOn={isCameraOn}
                isMicOn={isMicOn}
                facingMode={facingMode}
                videoQuality={activeStreamVideoQuality}
                orientation={activeStreamOrientation}
                eventId={eventId}
                audioMode={activeStreamAudioMode}
                speakerVolume={speakerVolume}
                micVolume={micVolume}
                balance={balance}
                treble={treble}
                bass={bass}
                onCameraSwitchTrigger={switchCameraRef}
                onFacingModeChange={setFacingMode}
                showMeFocusRef={showMeFocusRef}
                onShowMeFocusCapabilities={setShowMeFocusCapabilities}
                onPublisherReady={handlePublisherReady}
                onQualityStatusChange={setBroadcastQualityStatus}
              />
              <RoomAudioRenderer />
              <StreamDiagnostics label="Basic creator" />
            </ManualCreatorRoom>
          ) : (
            <CameraPreview
              isCameraOn={isCameraOn}
              facingMode={facingMode}
              videoQuality={videoQuality}
              orientation={deviceOrientation}
              showMeFocusRef={showMeFocusRef}
              onShowMeFocusCapabilities={setShowMeFocusCapabilities}
              cameraControlRef={previewCameraControlRef}
            />
          )}
        </ProtectedContent>
      </div>

      {/* ShowMeEnergy (reaction-powered edge glow) */}
      {showOverlays && event?.reactions_enabled !== false && (
        <ShowMeEnergy reactionCount={reactionCount} />
      )}

      <LiveTopBar
        eventId={eventId}
        creatorName={event?.creator_name || event?.creator?.username || event?.creator?.name || event?.creatorName || user?.username || user?.name || user?.email?.split("@")[0]}
        creatorPicture={user?.picture || event?.creator_picture || event?.creator?.picture}
        eventTitle={event?.title}
        viewerCount={viewerCount}
        streamTime={streamTime}
        isCreator
        isLive={isStreaming}
        onExit={handleCreatorExit}
        audienceSpeakerMode={(isEducation || isPodcast || isReligion) ? (isPodcast ? "podcast" : isReligion ? "religion" : "education") : null}
        audienceSpeakerEnabled={event?.audience_speaker_enabled !== false}
        onToggleAudienceSpeaker={toggleAudienceSpeakerAvailability}
        audienceSpeakerBusy={audienceSpeakerToggleBusy}
      />

      {/* Chat Messages (Left Side) */}
      {showOverlays && event?.chat_enabled !== false && !isEducation && (
        <LiveChatLane
          messages={chatMessages}
          connected={chatConnected}
          audience="creator"
          aboveChatControl={creatorEventIsLive && isAudienceSpeakerEvent && educationSpeakers.some((speaker) => ["waiting", "invited", "speaking", "muted"].includes(speaker.status)) ? (
            <CreatorAudienceSpeakerControls
              speakerRequests={educationSpeakers}
              onInviteSpeaker={inviteEducationSpeaker}
              onMuteSpeaker={muteEducationSpeaker}
              onEndSpeaker={endEducationSpeaker}
              speakerBusyId={educationSpeakerBusyId}
              speakerMode={isPodcast ? "podcast" : isReligion ? "religion" : "education"}
            />
          ) : null}
          onPinMessage={handlePinMessage}
          onMuteUser={handleMuteChatUser}
          onUnmuteUser={handleUnmuteChatUser}
          isShowMeCongregation={isShowMeExperience}
          conversationLabel={experience.chatLabel}
        />
      )}

      {creatorEventIsLive && isEducation && (
        <EducationQuestionQueue
          questions={educationQuestions}
          onDelete={deleteEducationQuestion}
          onPinQuestion={handlePinEducationQuestion}
          onMuteUser={handleMuteChatUser}
          onUnmuteUser={handleUnmuteChatUser}
          speakerRequests={educationSpeakers}
          onInviteSpeaker={inviteEducationSpeaker}
          onMuteSpeaker={muteEducationSpeaker}
          onEndSpeaker={endEducationSpeaker}
          speakerBusyId={educationSpeakerBusyId}
          speakerMode={isPodcast ? "podcast" : isReligion ? "religion" : "education"}
        />
      )}

      {/* Same smooth live reactions shown to viewers */}
      {showOverlays && event?.reactions_enabled !== false && (
        <LiveReactionLayer reactions={liveReactions} className="z-40" side={isShowMeExperience ? "right" : "spread"} />
      )}

      {/* Category-branded money effects share the right-side reaction lane. */}
      {event?.tips_enabled !== false && titheDollarEvents.length > 0 && (
        <ShowMeDonationDollarLayer events={titheDollarEvents} className="z-40" />
      )}

      {/* Creator-only running total. */}
      {creatorEventIsLive && event?.tips_enabled !== false && (
        <CreatorTitheBadge
          total={titheTotal}
          label={experience.tipsLabel.replace(/^ShowMe/, "")}
        />
      )}

      {creatorEventIsLive && experience.liveTipIdentity && (
        <CreatorTipNotificationStack notifications={tipNotifications} />
      )}

      {/* Transparent Bottom Controls */}
      <TransparentControls
        isCameraOn={isCameraOn}
        toggleCamera={toggleCamera}
        isMicOn={isMicOn}
        toggleMic={toggleMic}
        facingMode={facingMode}
        setFacingMode={setFacingMode}
        videoQuality={videoQuality}
        setVideoQuality={handleVideoQualityChange}
        audioMode={audioMode}
        setAudioMode={handleAudioModeChange}
        qualityStatus={broadcastQualityStatus}
        isStreaming={isStreaming}
        handleStartStream={requestStartStream}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        speakerVolume={speakerVolume}
        setSpeakerVolume={setSpeakerVolume}
        micVolume={micVolume}
        setMicVolume={setMicVolume}
        balance={balance}
        setBalance={setBalance}
        treble={treble}
        setTreble={setTreble}
        bass={bass}
        setBass={setBass}
        handleResetAudio={handleResetAudio}
        onCameraSwitchTrigger={switchCameraRef}
        showMeFocusRef={showMeFocusRef}
        showMeFocusCapabilities={showMeFocusCapabilities}
        rearLightOn={rearLightOn}
        setRearLightOn={setRearLightOn}
        onShare={handleOpenShareSheet}
      />

      <LiveShareSheet
        isOpen={showShareSheet}
        onClose={() => setShowShareSheet(false)}
        eventTitle={event?.title || "Live Stream"}
        eventUrl={`${window.location.origin}/event/${eventId}`}
        isCreator
      />
    </div>
  );
};

export default ControlPanel;
